import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useFloorPlan } from './hooks/useFloorPlan';
import { useCanvas } from './hooks/useCanvas';
import { useLayers } from './hooks/useLayers';
import { useCanvasInteraction } from './hooks/useCanvasInteraction';
import { Toolbar, MobileToolbar } from './components/toolbar';
import { TOOLBAR_ICONS } from './components/toolbar/ToolbarIcons';
import { FurnitureLibrary, StairsLibrary, ElectricalLibrary, PlumbingLibrary, FireSafetyLibrary, LayersPanel, SheetsPanel, SettingsPanel } from './components/sidebar';
import { WallEditor, RoofEditor, PostEditor, DoorEditor, WindowEditor, FurnitureEditor, FireSafetyEditor, TextEditor, PolylineEditor, HatchEditor, RoomEditor, TempDimensionEditor, WallTempDimensionEditor } from './components/editors';
import { DEFAULT_POST } from './constants/posts';
import { Button } from './components/ui';
import { FloorPlanCanvas } from './components/canvas';
import { ElevationView, ThreeDView, PaperSpaceView } from './components/views';
import { FileMenu, EditMenu, MobileMenu, SaveDialog, RecentProjects } from './components/menu';
import { MaterialCalculator } from './components/Materials';
import { saveAutoSave, loadAutoSave, saveAutoSaveAsset, loadAutoSaveAsset, AUTOSAVE_IMAGE_MARKER, AUTOSAVE_TERRAIN_MARKER, AUTOSAVE_INLINE_IMAGE_LIMIT, addToRecentProjects, getSavedProjectName, clearAutoSave, hasAutoSave, getRecentProjects } from './utils/storage';
import { calculateTotalFloorArea } from './utils/materialCalculations';
import { PDFImporter, PDFControls } from './components/pdf/PDFImporter';
import { PDFPageSidebar, PDFPageSidebarToggle } from './components/pdf/PDFPageSidebar';
import { ScaleCalibration } from './components/pdf/ScaleCalibration';
import { GradingPanel, ElevationReadout } from './components/grading';
import { ArenaPanelsPanel } from './components/panels/ArenaPanelsPanel';
import { SiteView3DBoundary } from './components/views/SiteView3DBoundary';
import { ARENA_PANEL_PRODUCTS, findPanelProduct } from './constants/arenaPanels';
import { usePanelTool } from './hooks/usePanelTool';
import { fenceOutline } from './utils/panelLayout';
import { ropingEndLayout, strippingEndLayout } from './utils/arenaEquipment';
import { parsePly, buildTerrainGrid, serializeTerrain, deserializeTerrain } from './utils/terrain';
import { DEFAULT_GRADING, collectPadCells, gradePad, buildCutFillOverlay, describeArena, arenaRoomFields, arenaAlleyRun, fitCornersToPanels, approximateArenaFrame, ARENA_PRESETS } from './utils/grading';
import { PDFMeasurementToolbar, PDFMeasurementTotals, usePDFMeasurements, calculateDistance, calculatePolygonArea, pixelsToInches, squarePixelsToSquareInches } from './components/pdf/PDFMeasurements';
import { PDFMeasurementRenderer } from './components/pdf/PDFMeasurementRenderer';
import { AnnotationToolbar, StampSelector, AnnotationSettingsPanel, CalloutNotesPanel } from './components/annotation';
import { useAnnotationTool } from './hooks/useAnnotationTool';
import { ExportModal } from './components/export';
import { PDFExportModal } from './components/export/PDFExportModal';
import { exportToPDF, exportToPNG, exportToJPEG } from './utils/exportPDF';
import { formatMeasurement } from './utils/measurements';
import { createDefaultSheet } from './constants/paper';
import { ShareModal } from './components/Share';
import { CompanyProfileModal } from './components/settings/CompanyProfileModal';
import { ClientManager } from './components/settings/ClientManager';
import { EstimateEditor, EstimateList, InvoiceEditor, InvoiceList } from './components/estimate';
import TemplateSelector from './components/templates/TemplateSelector';
import { HVACToolbar, DuctEditor, VentPlacer, VentEditor, EquipmentLibrary, EquipmentEditor, RoomDataPanel, HVACLayers, HVACSummary, HVACExportModal, HVACStamps, StampEditor, LabelPlacer, LabelEditor, LoadCalculationPanel, Ductulator, hitTestDuct, hitTestVent, hitTestEquipment, hitTestFitting, hitTestStamp, hitTestLabel, drawAllStamps, drawAllLabels, drawDuctSizeLabels, drawStampPreview, drawLabelPreview, drawAllRoomLoadLabels } from './components/HVAC';
import { StructuralToolbar, FloorJoistCalculator, BeamHeaderCalculator, JoistEditor } from './components/Structural';
import { hitTestJoist } from './components/canvas/renderers/joistRenderer';
import { DUCT_TYPES, DEFAULT_DUCT } from './constants/hvacOptions';
import { DEFAULT_DESIGN_CONDITIONS } from './constants/climateZones';
import { getStampById } from './constants/hvacStamps';
import { VENT_TYPES, EQUIPMENT_TYPES, DEFAULT_VENT, DEFAULT_EQUIPMENT } from './constants/hvacEquipment';
import OnboardingFlow, { shouldShowOnboarding } from './components/onboarding/OnboardingFlow';
import HelpPanel from './components/help/HelpPanel';
import { useProStatus, PRO_FEATURES } from './hooks/useProStatus';
import { ProBadge } from './components/settings/UpgradeModal';

/**
 * FloorPlan Pro - Main Application Component
 *
 * Full-featured floor plan editor with modular Vite + React architecture.
 */
// three.js is large - only load it when the 3D site view is opened
const SiteView3D = lazy(() => import('./components/views/SiteView3D'));

function App() {
  // Pro status for in-app purchases
  const { isPro, requirePro, canCreateProject } = useProStatus();

  // Device detection
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      // Check for touch capability
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      // Consider mobile if: small width OR (touch device with small height in landscape)
      // This handles phones in landscape mode which may have width > 768 but small height
      const smallerDimension = Math.min(width, height);
      const isMobileDevice = smallerDimension < 768 || (hasTouch && height < 500);
      setIsMobile(isMobileDevice);
      setIsTablet(!isMobileDevice && width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Floor plan state with undo/redo
  const {
    floors,
    activeFloorId,
    setActiveFloorId,
    activeFloor,
    updateActiveFloor,
    selectedItems,
    setSelectedItems,
    addWall,
    updateWall,
    removeWall,
    addDoor,
    addWindow,
    addFurniture,
    addFireSafety,
    updateFireSafety,
    removeFireSafety,
    addRoof,
    updateRoof,
    addPost,
    updatePost,
    removePost,
    removeRoof,
    addFloor,
    removeFloor,
    duplicateFloor,
    renameFloor,
    updateFloorElevation,
    updateWindowOnFloor,
    removeWindowOnFloor,
    updateDoorOnFloor,
    removeDoorOnFloor,
    undo,
    redo,
    canUndo,
    canRedo,
    newFile,
    loadFromTemplate,
    detectRooms,
    addRoom,
    updateRoom,
    exportToJSON,
    importFromJSON,
    pdfLayer,
    importPdfLayer,
    updatePdfLayer,
    removePdfLayer,
    calibratePdfScale,
    changePdfPage,
    relinkPdfDocument,
    annotations,
    annotationSettings,
    addAnnotation,
    updateAnnotation,
    removeAnnotation,
    clearAnnotations,
    updateAnnotationSettings,
    pdfMeasurements,
    setPdfMeasurements,
    addPdfMeasurement,
    updatePdfMeasurement,
    removePdfMeasurement,
    clearPdfMeasurements,
    // PDF page management
    rotatePdfPage,
    deletePdfPages,
    addBlankPdfPage,
    extractPdfPages,
    // Clipboard
    copySelected,
    paste,
    hasClipboard,
  } = useFloorPlan();

  // Canvas ref
  const canvasRef = useRef(null);

  // Last pointer position over the canvas, in screen coordinates. Paste uses
  // this to drop the copy where the cursor is. A ref rather than state so
  // tracking the pointer never triggers a re-render.
  const lastPointerRef = useRef(null);

  // Transient status-bar feedback for copy/paste
  const [clipboardMessage, setClipboardMessage] = useState(null);
  const clipboardMessageTimer = useRef(null);

  // Touch paste is a two-step gesture: choose Paste, then tap the spot.
  const [pendingPaste, setPendingPaste] = useState(false);

  // Whether the device can hover. This, not screen width, decides how paste
  // picks its spot: without hover there is no cursor to paste at. An iPad is
  // wide enough to miss the isMobile breakpoint but still has no pointer.
  const [canHover, setCanHover] = useState(true);
  useEffect(() => {
    const query = window.matchMedia?.('(hover: hover) and (pointer: fine)');
    if (!query) return;
    const update = () => setCanHover(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  // Whether the user's last click was on the canvas rather than in a field.
  // Selecting a wall auto-focuses its length input with the text selected, so
  // "an input has focus" does not mean the user is typing - without this,
  // Ctrl+C right after selecting something would always be swallowed.
  const clickedIntoFieldRef = useRef(false);

  useEffect(() => {
    const handlePointerDownAnywhere = (e) => {
      const t = e.target;
      clickedIntoFieldRef.current = !!t && (
        t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable === true
      );
    };
    document.addEventListener('pointerdown', handlePointerDownAnywhere, true);
    return () => document.removeEventListener('pointerdown', handlePointerDownAnywhere, true);
  }, []);

  // Canvas state
  const {
    containerRef,
    zoom,
    setZoom,
    offset,
    setOffset,
    screenToCanvas,
    handleWheel,
    startPan,
    updatePan,
    endPan,
    zoomIn,
    zoomOut,
    resetView,
    zoomToFit,
  } = useCanvas();

  // Layers state
  const {
    layers,
    toggleVisibility,
    toggleLock,
    setColor,
    showAll,
    hideAll,
    isVisible,
    isLocked,
  } = useLayers();

  // Tool state
  const [tool, setTool] = useState('wall');
  const [toolTab, setToolTab] = useState('draw');
  const [selectionMode, setSelectionMode] = useState('wall'); // 'wall' or 'room'
  const [wallType, setWallType] = useState('exterior');
  // Settings the Post tool places with; editing a selected post carries them forward
  const [postSettings, setPostSettings] = useState(DEFAULT_POST);
  const [wallDetailLevel, setWallDetailLevel] = useState('simple');
  const [gridSize, setGridSize] = useState('6"');
  const [angleSnap, setAngleSnap] = useState('45');
  const [showDimensions, setShowDimensions] = useState(true);
  const [showTempDimensions, setShowTempDimensions] = useState(true);
  const [showGrips, setShowGrips] = useState(true);
  const [thinLines, setThinLines] = useState(false);
  const [fontStyle, setFontStyle] = useState('modern');

  // Snap settings - which snap types are enabled
  const [snaps, setSnaps] = useState({
    endpoint: true,
    midpoint: true,
    perpendicular: true,
    nearest: true,
    grid: true,
  });

  // Track selection source: 'draw' (just created), 'click' (user clicked), or null
  // On mobile, we defer showing editors for 'draw' selections to avoid interrupting workflow
  const [selectionSource, setSelectionSource] = useState(null);

  // Track selected vertex index for polyline/hatch editors (for visual highlighting on canvas)
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);

  // Canvas interaction
  const {
    isDrawing,
    drawStart,
    drawEnd,
    isPanning,
    moveBasePoint,
    movePreviewPoint,
    rotateCenter,
    rotateStartAngle,
    rotatePreviewAngle,
    snapGuidelines,
    activeSnap,
    polarAngle,
    gripCursorPosition,
    polylinePoints,
    hatchPoints,
    roomPoints,
    windowSelection,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleDoubleClick,
    cancelPolyline,
    cancelHatch,
    cancelRoom,
    // Aliased: useCanvas exports a screenToCanvas of its own, but its ref is
    // never attached to the canvas element. This is the one that works.
    screenToCanvas: screenToWorld,
    findNearestWall,
  } = useCanvasInteraction({
    activeFloor,
    updateActiveFloor,
    selectedItems,
    setSelectedItems,
    setSelectionSource,
    canvasRef,
    scale: zoom,
    setScale: setZoom,
    offset,
    setOffset,
    tool,
    selectionMode,
    wallType,
    postSettings,
    gridSize,
    angleSnap,
    isMobile,
    layers,
    snaps,
  });

  // UI state
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [viewMode, setViewMode] = useState('model'); // 'model', 'paper', or 'elevations'
  const [show3D, setShow3D] = useState(false);
  const [showScaleCalibration, setShowScaleCalibration] = useState(false);
  const [isMovingPdf, setIsMovingPdf] = useState(false);
  const [pdfDragStart, setPdfDragStart] = useState(null);
  const [showPdfPageSidebar, setShowPdfPageSidebar] = useState(false);

  // PDF Measurement state
  const [activeMeasureTool, setActiveMeasureTool] = useState(null); // 'linear' | 'area' | null
  const [drawingMeasurement, setDrawingMeasurement] = useState(null); // { type, points, previewPoint }
  const [selectedMeasurementIds, setSelectedMeasurementIds] = useState([]);
  const [showMeasurementTotals, setShowMeasurementTotals] = useState(false);
  const [draggingMeasurementGrip, setDraggingMeasurementGrip] = useState(null); // { measurementId, pointIndex }

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showRecentProjects, setShowRecentProjects] = useState(false);
  const [showMaterialCalculator, setShowMaterialCalculator] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPDFExportModal, setShowPDFExportModal] = useState(false);
  const [showCompanyProfile, setShowCompanyProfile] = useState(false);
  const [showClientManager, setShowClientManager] = useState(false);
  const [showEstimateEditor, setShowEstimateEditor] = useState(false);
  const [showEstimateList, setShowEstimateList] = useState(false);
  const [estimateMaterials, setEstimateMaterials] = useState([]);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  // Site grading: existing ground from drone data and the arena pad design
  const [terrain, setTerrain] = useState(null);
  const [grading, setGrading] = useState(DEFAULT_GRADING);
  const [gradingResult, setGradingResult] = useState(null); // { result, overlay }
  // Textured drone model for the 3D view: { buffer: ArrayBuffer (GLB), fileName, size }.
  // Too big for project files, so it lives in the browser's storage only.
  const [siteModel, setSiteModel] = useState(null);
  const [missingSiteModel, setMissingSiteModel] = useState(null); // name of a model an opened project used
  const cursorListenerRef = useRef(null);
  // Arena / corral panels: product used by the Panel tool, and the auto-fence setup
  const [panelChoice, setPanelChoice] = useState(() => ({
    productId: ARENA_PANEL_PRODUCTS[0].id,
    optionLabel: ARENA_PANEL_PRODUCTS[0].options.find(o => o.kind === 'panel')?.label,
  }));
  const [panelFence, setPanelFence] = useState(() => ({
    roomId: null,
    panelLabel: ARENA_PANEL_PRODUCTS[0].options.find(o => o.kind === 'panel')?.label,
    gates: [],
  }));
  const [fenceReport, setFenceReport] = useState(null);
  const [showSite3D, setShowSite3D] = useState(false);
  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false);
  const [showInvoiceList, setShowInvoiceList] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [sourceEstimateForInvoice, setSourceEstimateForInvoice] = useState(null);
  const [projectName, setProjectName] = useState(() => getSavedProjectName());

  // Onboarding and Help
  const [showOnboarding, setShowOnboarding] = useState(() => shouldShowOnboarding());
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showFloorSelector, setShowFloorSelector] = useState(false);
  const [hasRestoredAutoSave, setHasRestoredAutoSave] = useState(false);

  // Elevation view settings (persists between view switches)
  const [elevationSettings, setElevationSettings] = useState({
    activeElevation: 'north',
    renderMode: 'color',
    sidingPattern: 'horizontal',
    foundationHeight: 12,
    showDimensions: false,
    selectedScale: 'auto',
    showLevelLines: true,
  });

  // HVAC mode state
  const [hvacMode, setHvacMode] = useState(false);
  const [hvacTool, setHvacTool] = useState('select');
  const [ductType, setDuctType] = useState('supply');
  const [ductShape, setDuctShape] = useState('trunk');
  const [ductSize, setDuctSize] = useState(6);
  const [ductPreviewPoints, setDuctPreviewPoints] = useState([]);
  const [selectedDuctId, setSelectedDuctId] = useState(null);
  const [isDrawingDuct, setIsDrawingDuct] = useState(false);
  const [draggingDuctPoint, setDraggingDuctPoint] = useState(null); // { ductId, pointIndex }

  // Vent state
  const [ventType, setVentType] = useState('supply-floor');
  const [ventSize, setVentSize] = useState('6x12');
  const [ventRotation, setVentRotation] = useState(0);
  const [selectedVentId, setSelectedVentId] = useState(null);
  const [showVentPlacer, setShowVentPlacer] = useState(false);

  // Equipment state
  const [equipmentType, setEquipmentType] = useState('furnace-gas');
  const [equipmentSize, setEquipmentSize] = useState('80K');
  const [equipmentRotation, setEquipmentRotation] = useState(0);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [showEquipmentLibrary, setShowEquipmentLibrary] = useState(false);

  // HVAC preview position (for vent/equipment placement preview)
  const [hvacPreviewPosition, setHvacPreviewPosition] = useState(null);

  // Fitting state
  const [fittingType, setFittingType] = useState('elbow');
  const [fittingRotation, setFittingRotation] = useState(0);
  const [selectedFittingId, setSelectedFittingId] = useState(null);

  // Room Data state (for Manual J)
  const [showRoomDataPanel, setShowRoomDataPanel] = useState(false);
  const [selectedRoomForData, setSelectedRoomForData] = useState(null);

  // HVAC Layers, Summary, and Export panels
  const [showHVACLayers, setShowHVACLayers] = useState(false);
  const [showHVACSummary, setShowHVACSummary] = useState(false);
  const [showHVACExport, setShowHVACExport] = useState(false);
  const [hvacLayerState, setHvacLayerState] = useState({
    supplyDucts: { visible: true, locked: false, opacity: 1 },
    returnDucts: { visible: true, locked: false, opacity: 1 },
    supplyVents: { visible: true, locked: false, opacity: 1 },
    returnGrilles: { visible: true, locked: false, opacity: 1 },
    equipment: { visible: true, locked: false, opacity: 1 },
    fittings: { visible: true, locked: false, opacity: 1 },
    cfmLabels: { visible: true, locked: false, opacity: 1 },
    roomDataLabels: { visible: true, locked: false, opacity: 1 },
    stamps: { visible: true, locked: false, opacity: 1 },
  });

  // HVAC Stamps and Labels
  const [showHVACStamps, setShowHVACStamps] = useState(false);
  const [showLabelPlacer, setShowLabelPlacer] = useState(false);
  const [labelPlacerType, setLabelPlacerType] = useState('CFM');
  const [selectedStampType, setSelectedStampType] = useState(null);
  const [pendingLabel, setPendingLabel] = useState(null);
  const [showLabelsOnDucts, setShowLabelsOnDucts] = useState(true);
  const [selectedHvacStamp, setSelectedHvacStamp] = useState(null);
  const [selectedHvacLabel, setSelectedHvacLabel] = useState(null);
  const [draggingHvacItem, setDraggingHvacItem] = useState(null); // { type: 'stamp'|'label', id, offset: {x, y} }

  // Load Calculations
  const [showLoadCalcPanel, setShowLoadCalcPanel] = useState(false);
  const [designConditions, setDesignConditions] = useState(DEFAULT_DESIGN_CONDITIONS);
  const [showLoadLabels, setShowLoadLabels] = useState(true);

  // Ductulator
  const [showDuctulator, setShowDuctulator] = useState(false);

  // Structural mode state
  const [structuralMode, setStructuralMode] = useState(false);
  const [structuralTool, setStructuralTool] = useState('select');
  const [buildingType, setBuildingType] = useState('residential');
  const [showJoistCalc, setShowJoistCalc] = useState(false);
  const [showBeamCalc, setShowBeamCalc] = useState(false);
  const [selectedJoistId, setSelectedJoistId] = useState(null);
  const [isDrawingJoist, setIsDrawingJoist] = useState(false);
  const [joistDrawStart, setJoistDrawStart] = useState(null);
  const [joistDrawEnd, setJoistDrawEnd] = useState(null);
  const [joistDefaults, setJoistDefaults] = useState({
    direction: 'horizontal', spacing: 16, size: '2x10',
    species: 'spf', grade: '#2', engineered: null,
    color: '#8B6914', opacity: 0.6, showLabel: true,
  });

  // Annotation state
  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState([]);
  const [annotationMode, setAnnotationMode] = useState(false); // Whether annotation tools are active

  // Annotation tool hook
  const {
    isDrawing: isDrawingAnnotation,
    drawingAnnotation,
    handleAnnotationPointerDown,
    handleAnnotationPointerMove,
    handleAnnotationPointerUp,
    handleAnnotationDoubleClick,
    cancelCloudDrawing,
    deleteSelectedAnnotations,
    resizeSelectedStamp,
  } = useAnnotationTool({
    annotations,
    addAnnotation,
    updateAnnotation,
    removeAnnotation,
    annotationSettings,
    selectedAnnotations: selectedAnnotationIds,
    setSelectedAnnotations: setSelectedAnnotationIds,
    scale: zoom,
    setScale: setZoom,
    offset,
    setOffset,
    canvasRef,
  });

  // Units
  const [units, setUnits] = useState('imperial');

  // Sheets state for Paper Space
  const [sheets, setSheets] = useState([]);
  const [activeSheetId, setActiveSheetId] = useState(null);

  // Project info for title blocks
  const [projectInfo, setProjectInfo] = useState({
    projectName: '',
    clientName: '',
    address: '',
    drawnBy: '',
    date: new Date().toLocaleDateString(),
  });

  // Get active sheet
  const activeSheet = sheets.find(s => s.id === activeSheetId) || null;

  // Sheet management functions
  const generateId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

  const handleAddSheet = useCallback(() => {
    const newSheet = createDefaultSheet(generateId(), activeFloorId);
    newSheet.sheetNumber = `A${String(sheets.length + 1).padStart(2, '0')}`;
    setSheets(prev => [...prev, newSheet]);
    setActiveSheetId(newSheet.id);
  }, [sheets.length, activeFloorId]);

  const handleUpdateSheet = useCallback((sheetId, updates) => {
    setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, ...updates } : s));
  }, []);

  const handleDeleteSheet = useCallback((sheetId) => {
    setSheets(prev => prev.filter(s => s.id !== sheetId));
    if (activeSheetId === sheetId) {
      setActiveSheetId(sheets[0]?.id || null);
    }
  }, [activeSheetId, sheets]);

  const handleSelectSheet = useCallback((sheetId) => {
    setActiveSheetId(sheetId);
    setViewMode('paper');
  }, []);

  // HVAC mode handlers
  const toggleHvacMode = useCallback(() => {
    setHvacMode(prev => !prev);
    if (!hvacMode) {
      setHvacTool('duct');
      setSelectedDuctId(null);
      setStructuralMode(false); // mutual exclusivity
    }
  }, [hvacMode]);

  const toggleStructuralMode = useCallback(() => {
    setStructuralMode(prev => {
      if (!prev) {
        setHvacMode(false); // mutual exclusivity
      }
      return !prev;
    });
    setStructuralTool('select');
    setSelectedJoistId(null);
    setIsDrawingJoist(false);
  }, []);

  // Joist CRUD handlers
  const handleAddJoist = useCallback((joistData) => {
    const newJoist = {
      id: generateId(),
      ...joistDefaults,
      ...joistData,
    };
    updateActiveFloor(f => ({
      ...f,
      joists: [...(f.joists || []), newJoist],
    }));
    setSelectedJoistId(newJoist.id);
    setStructuralTool('select');
    return newJoist;
  }, [joistDefaults, updateActiveFloor, generateId]);

  const handleUpdateJoist = useCallback((joistId, updates) => {
    if (!activeFloor?.joists) return;
    updateActiveFloor(f => ({
      ...f,
      joists: f.joists.map(j => j.id === joistId ? { ...j, ...updates } : j),
    }));
    // Sync defaults so next drawn bay inherits settings
    const syncKeys = ['direction', 'spacing', 'size', 'species', 'grade', 'engineered', 'color', 'opacity', 'showLabel'];
    const defaultUpdates = Object.fromEntries(
      Object.entries(updates).filter(([k]) => syncKeys.includes(k))
    );
    if (Object.keys(defaultUpdates).length > 0) {
      setJoistDefaults(prev => ({ ...prev, ...defaultUpdates }));
    }
  }, [activeFloor, updateActiveFloor]);

  const handleDeleteJoist = useCallback((joistId) => {
    if (!activeFloor?.joists) return;
    updateActiveFloor(f => ({
      ...f,
      joists: f.joists.filter(j => j.id !== joistId),
    }));
    if (selectedJoistId === joistId) setSelectedJoistId(null);
  }, [activeFloor, updateActiveFloor, selectedJoistId]);

  // Structural pointer handlers
  const handleStructuralPointerDown = useCallback((e) => {
    if (!structuralMode) return false;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;
    const x = (e.clientX - rect.left - offset.x) / zoom;
    const y = (e.clientY - rect.top - offset.y) / zoom;

    if (structuralTool === 'joist') {
      setIsDrawingJoist(true);
      setJoistDrawStart({ x, y });
      setJoistDrawEnd({ x, y });
      return true;
    }

    if (structuralTool === 'select') {
      const hit = hitTestJoist(x, y, activeFloor?.joists);
      if (hit) {
        setSelectedJoistId(hit.id);
        return true;
      }
      setSelectedJoistId(null);
      return false; // Let pan handler take over
    }

    return false;
  }, [structuralMode, structuralTool, offset, zoom, activeFloor?.joists]);

  const handleStructuralPointerMove = useCallback((e) => {
    if (!structuralMode || !isDrawingJoist || !joistDrawStart) return false;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;
    const x = (e.clientX - rect.left - offset.x) / zoom;
    const y = (e.clientY - rect.top - offset.y) / zoom;

    setJoistDrawEnd({ x, y });
    return true;
  }, [structuralMode, isDrawingJoist, joistDrawStart, offset, zoom]);

  const handleStructuralPointerUp = useCallback((e) => {
    if (!structuralMode || !isDrawingJoist || !joistDrawStart || !joistDrawEnd) return false;

    const minSize = 20;
    const w = Math.abs(joistDrawEnd.x - joistDrawStart.x);
    const h = Math.abs(joistDrawEnd.y - joistDrawStart.y);

    if (w > minSize && h > minSize) {
      handleAddJoist({
        startX: joistDrawStart.x,
        startY: joistDrawStart.y,
        endX: joistDrawEnd.x,
        endY: joistDrawEnd.y,
        // Auto-detect direction: joists span the shorter dimension
        direction: w >= h ? 'vertical' : 'horizontal',
      });
    }

    setIsDrawingJoist(false);
    setJoistDrawStart(null);
    setJoistDrawEnd(null);
    return true;
  }, [structuralMode, isDrawingJoist, joistDrawStart, joistDrawEnd, handleAddJoist]);

  const handleHvacToolChange = useCallback((newTool) => {
    setHvacTool(newTool);
    setIsDrawingDuct(false);
    setDuctPreviewPoints([]);
    // Clear selections when changing tools
    setSelectedHvacStamp(null);
    setSelectedHvacLabel(null);
    // Clear preview position when switching to tools that don't use it
    if (newTool !== 'supply-vent' && newTool !== 'return-vent' && newTool !== 'equipment' &&
        newTool !== 'stamp' && newTool !== 'cfm-label' && newTool !== 'duct-label') {
      setHvacPreviewPosition(null);
    }
    // Clear pending label when not placing labels
    if (newTool !== 'cfm-label' && newTool !== 'duct-label') {
      setPendingLabel(null);
    }
    // Clear stamp type when not placing stamps
    if (newTool !== 'stamp') {
      setSelectedStampType(null);
    }
  }, []);

  const handleAddDuct = useCallback((ductData) => {
    const newDuct = {
      id: generateId(),
      ...DEFAULT_DUCT,
      ...ductData,
    };
    updateActiveFloor({
      ...activeFloor,
      ducts: [...(activeFloor.ducts || []), newDuct],
    });
    return newDuct;
  }, [activeFloor, updateActiveFloor]);

  const handleUpdateDuct = useCallback((ductId, updates) => {
    if (!activeFloor?.ducts) return;
    updateActiveFloor({
      ...activeFloor,
      ducts: activeFloor.ducts.map(d => d.id === ductId ? { ...d, ...updates } : d),
    });
  }, [activeFloor, updateActiveFloor]);

  const handleDeleteDuct = useCallback((ductId) => {
    if (!activeFloor?.ducts) return;
    updateActiveFloor({
      ...activeFloor,
      ducts: activeFloor.ducts.filter(d => d.id !== ductId),
    });
    if (selectedDuctId === ductId) {
      setSelectedDuctId(null);
    }
  }, [activeFloor, updateActiveFloor, selectedDuctId]);

  // Vent handlers
  const handleAddVent = useCallback((ventData) => {
    const newVent = {
      id: generateId(),
      type: ventType,
      size: ventSize,
      rotation: ventRotation,
      cfm: null,
      ...ventData,
    };
    updateActiveFloor({
      ...activeFloor,
      vents: [...(activeFloor.vents || []), newVent],
    });
    setSelectedVentId(newVent.id);
    return newVent;
  }, [activeFloor, updateActiveFloor, ventType, ventSize, ventRotation]);

  const handleUpdateVent = useCallback((ventId, updates) => {
    if (!activeFloor?.vents) return;
    updateActiveFloor({
      ...activeFloor,
      vents: activeFloor.vents.map(v => v.id === ventId ? { ...v, ...updates } : v),
    });
  }, [activeFloor, updateActiveFloor]);

  const handleDeleteVent = useCallback((ventId) => {
    if (!activeFloor?.vents) return;
    updateActiveFloor({
      ...activeFloor,
      vents: activeFloor.vents.filter(v => v.id !== ventId),
    });
    if (selectedVentId === ventId) {
      setSelectedVentId(null);
    }
  }, [activeFloor, updateActiveFloor, selectedVentId]);

  // Equipment handlers
  const handleAddEquipment = useCallback((equipData) => {
    const newEquipment = {
      id: generateId(),
      type: equipmentType,
      size: equipmentSize,
      rotation: equipmentRotation,
      ...equipData,
    };
    updateActiveFloor({
      ...activeFloor,
      equipment: [...(activeFloor.equipment || []), newEquipment],
    });
    setSelectedEquipmentId(newEquipment.id);
    return newEquipment;
  }, [activeFloor, updateActiveFloor, equipmentType, equipmentSize, equipmentRotation]);

  const handleUpdateEquipment = useCallback((equipId, updates) => {
    if (!activeFloor?.equipment) return;
    updateActiveFloor({
      ...activeFloor,
      equipment: activeFloor.equipment.map(e => e.id === equipId ? { ...e, ...updates } : e),
    });
  }, [activeFloor, updateActiveFloor]);

  const handleDeleteEquipment = useCallback((equipId) => {
    if (!activeFloor?.equipment) return;
    updateActiveFloor({
      ...activeFloor,
      equipment: activeFloor.equipment.filter(e => e.id !== equipId),
    });
    if (selectedEquipmentId === equipId) {
      setSelectedEquipmentId(null);
    }
  }, [activeFloor, updateActiveFloor, selectedEquipmentId]);

  // Fitting handlers
  const handleAddFitting = useCallback((fittingData) => {
    const newFitting = {
      id: generateId(),
      type: fittingType,
      ductType: ductType,
      size: ductSize,
      rotation: fittingRotation,
      ...fittingData,
    };
    updateActiveFloor({
      ...activeFloor,
      fittings: [...(activeFloor.fittings || []), newFitting],
    });
    setSelectedFittingId(newFitting.id);
    return newFitting;
  }, [activeFloor, updateActiveFloor, fittingType, ductType, ductSize, fittingRotation]);

  const handleUpdateFitting = useCallback((fittingId, updates) => {
    if (!activeFloor?.fittings) return;
    updateActiveFloor({
      ...activeFloor,
      fittings: activeFloor.fittings.map(f => f.id === fittingId ? { ...f, ...updates } : f),
    });
  }, [activeFloor, updateActiveFloor]);

  const handleDeleteFitting = useCallback((fittingId) => {
    if (!activeFloor?.fittings) return;
    updateActiveFloor({
      ...activeFloor,
      fittings: activeFloor.fittings.filter(f => f.id !== fittingId),
    });
    if (selectedFittingId === fittingId) {
      setSelectedFittingId(null);
    }
  }, [activeFloor, updateActiveFloor, selectedFittingId]);

  // Room data handlers (for Manual J)
  const handleUpdateRoomData = useCallback((roomData) => {
    if (!activeFloor) return;
    const existingRoomData = activeFloor.roomData || [];
    const existingIndex = existingRoomData.findIndex(rd => rd.roomId === roomData.roomId);
    let newRoomData;
    if (existingIndex >= 0) {
      newRoomData = [...existingRoomData];
      newRoomData[existingIndex] = roomData;
    } else {
      newRoomData = [...existingRoomData, roomData];
    }
    updateActiveFloor({
      ...activeFloor,
      roomData: newRoomData,
    });
  }, [activeFloor, updateActiveFloor]);

  const handleOpenRoomData = useCallback((room) => {
    setSelectedRoomForData(room);
    setShowRoomDataPanel(true);
  }, []);

  // HVAC Stamp handlers
  const handleAddStamp = useCallback((stampType, position) => {
    const newStamp = {
      id: generateId(),
      type: 'stamp',
      stampType: stampType,
      position: position,
      rotation: 0,
    };
    updateActiveFloor({
      ...activeFloor,
      hvacStamps: [...(activeFloor.hvacStamps || []), newStamp],
    });
    setSelectedHvacStamp(newStamp.id);
    return newStamp;
  }, [activeFloor, updateActiveFloor]);

  const handleUpdateStamp = useCallback((stampId, updates) => {
    if (!activeFloor?.hvacStamps) return;
    updateActiveFloor({
      ...activeFloor,
      hvacStamps: activeFloor.hvacStamps.map(s => s.id === stampId ? { ...s, ...updates } : s),
    });
  }, [activeFloor, updateActiveFloor]);

  const handleDeleteStamp = useCallback((stampId) => {
    if (!activeFloor?.hvacStamps) return;
    updateActiveFloor({
      ...activeFloor,
      hvacStamps: activeFloor.hvacStamps.filter(s => s.id !== stampId),
    });
    if (selectedHvacStamp === stampId) {
      setSelectedHvacStamp(null);
    }
  }, [activeFloor, updateActiveFloor, selectedHvacStamp]);

  // HVAC Label handlers
  const handleAddLabel = useCallback((labelConfig, position) => {
    const newLabel = {
      id: generateId(),
      type: labelConfig.type,
      value: labelConfig.value,
      position: position,
      showArrow: labelConfig.showArrow || false,
      arrowTarget: labelConfig.showArrow ? { x: position.x + 50, y: position.y } : null,
    };
    updateActiveFloor({
      ...activeFloor,
      hvacLabels: [...(activeFloor.hvacLabels || []), newLabel],
    });
    setSelectedHvacLabel(newLabel.id);
    setPendingLabel(null);
    return newLabel;
  }, [activeFloor, updateActiveFloor]);

  const handleUpdateLabel = useCallback((labelId, updates) => {
    if (!activeFloor?.hvacLabels) return;
    updateActiveFloor({
      ...activeFloor,
      hvacLabels: activeFloor.hvacLabels.map(l => l.id === labelId ? { ...l, ...updates } : l),
    });
  }, [activeFloor, updateActiveFloor]);

  const handleDeleteLabel = useCallback((labelId) => {
    if (!activeFloor?.hvacLabels) return;
    updateActiveFloor({
      ...activeFloor,
      hvacLabels: activeFloor.hvacLabels.filter(l => l.id !== labelId),
    });
    if (selectedHvacLabel === labelId) {
      setSelectedHvacLabel(null);
    }
  }, [activeFloor, updateActiveFloor, selectedHvacLabel]);

  // Helper to find nearest duct endpoint for fitting snapping
  const FITTING_SNAP_DISTANCE = 30; // World units (inches) - snap within 30"

  const findNearestDuctEndpoint = useCallback((worldX, worldY) => {
    if (!activeFloor?.ducts || activeFloor.ducts.length === 0) return null;

    let nearest = null;
    let nearestDist = FITTING_SNAP_DISTANCE;

    for (const duct of activeFloor.ducts) {
      if (!duct.points || duct.points.length < 2) continue;

      // Check start endpoint
      const startPt = duct.points[0];
      const startDist = Math.sqrt((worldX - startPt.x) ** 2 + (worldY - startPt.y) ** 2);
      if (startDist < nearestDist) {
        nearestDist = startDist;
        // Calculate angle from first to second point (direction duct is going)
        const nextPt = duct.points[1];
        const angle = Math.atan2(nextPt.y - startPt.y, nextPt.x - startPt.x) * 180 / Math.PI;
        nearest = {
          x: startPt.x,
          y: startPt.y,
          angle: angle,
          ductId: duct.id,
          pointIndex: 0,
          ductType: duct.type,
          ductSize: duct.size
        };
      }

      // Check end endpoint
      const endPt = duct.points[duct.points.length - 1];
      const endDist = Math.sqrt((worldX - endPt.x) ** 2 + (worldY - endPt.y) ** 2);
      if (endDist < nearestDist) {
        nearestDist = endDist;
        // Calculate angle from last to second-to-last point (direction duct is going)
        const prevPt = duct.points[duct.points.length - 2];
        const angle = Math.atan2(prevPt.y - endPt.y, prevPt.x - endPt.x) * 180 / Math.PI;
        nearest = {
          x: endPt.x,
          y: endPt.y,
          angle: angle,
          ductId: duct.id,
          pointIndex: duct.points.length - 1,
          ductType: duct.type,
          ductSize: duct.size
        };
      }
    }

    return nearest;
  }, [activeFloor?.ducts]);

  // Helper to find if clicking on a duct endpoint
  const findDuctPointAt = useCallback((worldX, worldY) => {
    if (!selectedDuctId || !activeFloor?.ducts) return null;

    const duct = activeFloor.ducts.find(d => d.id === selectedDuctId);
    if (!duct || !duct.points) return null;

    // Hit radius in world coordinates (adjusts with zoom for consistent feel)
    const hitRadius = Math.max(10, 10 / zoom);

    for (let i = 0; i < duct.points.length; i++) {
      const pt = duct.points[i];
      const dist = Math.sqrt((worldX - pt.x) ** 2 + (worldY - pt.y) ** 2);
      if (dist <= hitRadius) {
        return { ductId: duct.id, pointIndex: i };
      }
    }
    return null;
  }, [selectedDuctId, activeFloor, zoom]);

  // HVAC pointer handlers
  const handleHvacPointerDown = useCallback((e) => {
    if (!hvacMode) return false;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const x = (e.clientX - rect.left - offset.x) / zoom;
    const y = (e.clientY - rect.top - offset.y) / zoom;

    if (hvacTool === 'duct') {
      setIsDrawingDuct(true);
      setDuctPreviewPoints([{ x, y }]);
      return true;
    }

    // Handle vent placement - but first check if clicking on existing item
    if (hvacTool === 'supply-vent' || hvacTool === 'return-vent') {
      // Check if clicking on existing equipment first
      const equipment = activeFloor?.equipment || [];
      for (const equip of equipment) {
        if (hitTestEquipment(x, y, equip, zoom)) {
          setSelectedEquipmentId(equip.id);
          setSelectedVentId(null);
          setSelectedDuctId(null);
          setHvacTool('select');
          return true;
        }
      }
      // Check if clicking on existing vent
      const vents = activeFloor?.vents || [];
      for (const vent of vents) {
        if (hitTestVent(x, y, vent, zoom)) {
          setSelectedVentId(vent.id);
          setSelectedEquipmentId(null);
          setSelectedDuctId(null);
          setHvacTool('select');
          return true;
        }
      }
      // Check if clicking on existing duct
      const ducts = activeFloor?.ducts || [];
      for (const duct of ducts) {
        if (hitTestDuct(x, y, duct, zoom)) {
          setSelectedDuctId(duct.id);
          setSelectedVentId(null);
          setSelectedEquipmentId(null);
          setHvacTool('select');
          return true;
        }
      }
      // No existing item clicked, place new vent
      handleAddVent({ x, y });
      setHvacTool('select'); // Switch to select after placing
      return true;
    }

    // Handle equipment placement - but first check if clicking on existing item
    if (hvacTool === 'equipment') {
      // Check if clicking on existing equipment first
      const equipment = activeFloor?.equipment || [];
      for (const equip of equipment) {
        if (hitTestEquipment(x, y, equip, zoom)) {
          setSelectedEquipmentId(equip.id);
          setSelectedVentId(null);
          setSelectedDuctId(null);
          setHvacTool('select');
          return true;
        }
      }
      // Check if clicking on existing vent
      const vents = activeFloor?.vents || [];
      for (const vent of vents) {
        if (hitTestVent(x, y, vent, zoom)) {
          setSelectedVentId(vent.id);
          setSelectedEquipmentId(null);
          setSelectedDuctId(null);
          setHvacTool('select');
          return true;
        }
      }
      // Check if clicking on existing duct
      const ducts = activeFloor?.ducts || [];
      for (const duct of ducts) {
        if (hitTestDuct(x, y, duct, zoom)) {
          setSelectedDuctId(duct.id);
          setSelectedVentId(null);
          setSelectedEquipmentId(null);
          setHvacTool('select');
          return true;
        }
      }
      // No existing item clicked, place new equipment
      handleAddEquipment({ x, y });
      setHvacTool('select'); // Switch to select after placing
      return true;
    }

    // Handle elbow fitting placement
    if (hvacTool === 'elbow') {
      // Check if clicking on existing fitting first
      const fittings = activeFloor?.fittings || [];
      for (const fitting of fittings) {
        if (hitTestFitting(x, y, fitting, zoom)) {
          setSelectedFittingId(fitting.id);
          setSelectedDuctId(null);
          setSelectedVentId(null);
          setSelectedEquipmentId(null);
          setHvacTool('select');
          return true;
        }
      }
      // Try to snap to nearest duct endpoint
      const snapPoint = findNearestDuctEndpoint(x, y);
      if (snapPoint) {
        // Snap to duct endpoint with auto-rotation
        handleAddFitting({
          x: snapPoint.x,
          y: snapPoint.y,
          type: 'elbow',
          rotation: snapPoint.angle,
          ductType: snapPoint.ductType,
          size: snapPoint.ductSize
        });
      } else {
        // Place at cursor position if not near a duct
        handleAddFitting({ x, y, type: 'elbow' });
      }
      setHvacTool('select'); // Switch to select after placing
      return true;
    }

    // Handle tee fitting placement
    if (hvacTool === 'tee') {
      // Check if clicking on existing fitting first
      const fittings = activeFloor?.fittings || [];
      for (const fitting of fittings) {
        if (hitTestFitting(x, y, fitting, zoom)) {
          setSelectedFittingId(fitting.id);
          setSelectedDuctId(null);
          setSelectedVentId(null);
          setSelectedEquipmentId(null);
          setHvacTool('select');
          return true;
        }
      }
      // Try to snap to nearest duct endpoint
      const snapPoint = findNearestDuctEndpoint(x, y);
      if (snapPoint) {
        // Snap to duct endpoint with auto-rotation
        handleAddFitting({
          x: snapPoint.x,
          y: snapPoint.y,
          type: 'tee',
          rotation: snapPoint.angle,
          ductType: snapPoint.ductType,
          size: snapPoint.ductSize
        });
      } else {
        // Place at cursor position if not near a duct
        handleAddFitting({ x, y, type: 'tee' });
      }
      setHvacTool('select'); // Switch to select after placing
      return true;
    }

    // For select tool, check if clicking on a duct endpoint first
    if (hvacTool === 'select') {
      const pointHit = findDuctPointAt(x, y);
      if (pointHit) {
        setDraggingDuctPoint(pointHit);
        return true;
      }
      // Also do selection on pointerDown for immediate feedback
      // Check if clicked on equipment
      const equipment = activeFloor?.equipment || [];
      for (const equip of equipment) {
        if (hitTestEquipment(x, y, equip, zoom)) {
          setSelectedEquipmentId(equip.id);
          setSelectedVentId(null);
          setSelectedDuctId(null);
          return true;
        }
      }
      // Check if clicked on a vent
      const vents = activeFloor?.vents || [];
      for (const vent of vents) {
        if (hitTestVent(x, y, vent, zoom)) {
          setSelectedVentId(vent.id);
          setSelectedEquipmentId(null);
          setSelectedDuctId(null);
          return true;
        }
      }
      // Check if clicked on a duct
      const ducts = activeFloor?.ducts || [];
      for (const duct of ducts) {
        if (hitTestDuct(x, y, duct, zoom)) {
          setSelectedDuctId(duct.id);
          setSelectedVentId(null);
          setSelectedEquipmentId(null);
          setSelectedFittingId(null);
          return true;
        }
      }
      // Check if clicked on a fitting
      const fittings = activeFloor?.fittings || [];
      for (const fitting of fittings) {
        if (hitTestFitting(x, y, fitting, zoom)) {
          setSelectedFittingId(fitting.id);
          setSelectedDuctId(null);
          setSelectedVentId(null);
          setSelectedEquipmentId(null);
          setSelectedHvacStamp(null);
          setSelectedHvacLabel(null);
          return true;
        }
      }
      // Check if clicked on a stamp
      const stamps = activeFloor?.hvacStamps || [];
      for (const stamp of stamps) {
        if (hitTestStamp(stamp, { x, y })) {
          // Start dragging if clicking on the stamp
          setDraggingHvacItem({
            type: 'stamp',
            id: stamp.id,
            offset: { x: x - stamp.position.x, y: y - stamp.position.y }
          });
          setSelectedHvacStamp(stamp.id);
          setSelectedHvacLabel(null);
          setSelectedDuctId(null);
          setSelectedVentId(null);
          setSelectedEquipmentId(null);
          setSelectedFittingId(null);
          return true;
        }
      }
      // Check if clicked on a label
      const labels = activeFloor?.hvacLabels || [];
      for (const label of labels) {
        const hit = hitTestLabel(label, { x, y });
        if (hit.hit) {
          // Start dragging if clicking on the label
          setDraggingHvacItem({
            type: 'label',
            id: label.id,
            offset: { x: x - label.position.x, y: y - label.position.y }
          });
          setSelectedHvacLabel(label.id);
          setSelectedHvacStamp(null);
          setSelectedDuctId(null);
          setSelectedVentId(null);
          setSelectedEquipmentId(null);
          setSelectedFittingId(null);
          return true;
        }
      }
      // Clicked on nothing - clear selection and fall through to normal pan/zoom
      setSelectedDuctId(null);
      setSelectedVentId(null);
      setSelectedEquipmentId(null);
      setSelectedFittingId(null);
      setSelectedHvacStamp(null);
      setSelectedHvacLabel(null);
      return false;
    }

    // Handle room-data tool - click on room to open its data panel
    if (hvacTool === 'room-data') {
      const rooms = activeFloor?.rooms || [];
      for (const room of rooms) {
        if (room.vertices && room.vertices.length >= 3) {
          // Simple point-in-polygon test
          let inside = false;
          for (let i = 0, j = room.vertices.length - 1; i < room.vertices.length; j = i++) {
            const xi = room.vertices[i].x, yi = room.vertices[i].y;
            const xj = room.vertices[j].x, yj = room.vertices[j].y;
            if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
              inside = !inside;
            }
          }
          if (inside) {
            handleOpenRoomData(room);
            return true;
          }
        }
      }
    }

    // Handle stamp placement
    if (hvacTool === 'stamp' && selectedStampType) {
      // First check if clicking on existing stamp
      const stamps = activeFloor?.hvacStamps || [];
      for (const stamp of stamps) {
        if (hitTestStamp(stamp, { x, y })) {
          setSelectedHvacStamp(stamp.id);
          setSelectedHvacLabel(null);
          setHvacTool('select');
          return true;
        }
      }
      // Place new stamp
      handleAddStamp(selectedStampType, { x, y });
      // Keep stamp tool active for multiple placements
      return true;
    }

    // Handle CFM label placement
    if ((hvacTool === 'cfm-label' || hvacTool === 'duct-label') && pendingLabel) {
      // First check if clicking on existing label
      const labels = activeFloor?.hvacLabels || [];
      for (const label of labels) {
        const hit = hitTestLabel(label, { x, y });
        if (hit.hit) {
          setSelectedHvacLabel(label.id);
          setSelectedHvacStamp(null);
          setHvacTool('select');
          return true;
        }
      }
      // Place new label
      handleAddLabel(pendingLabel, { x, y });
      setHvacTool('select');
      return true;
    }

    return false;
  }, [hvacMode, hvacTool, zoom, offset, findDuctPointAt, findNearestDuctEndpoint, handleAddVent, handleAddEquipment, handleAddFitting, activeFloor, handleOpenRoomData, selectedStampType, pendingLabel, handleAddStamp, handleAddLabel]);

  const handleHvacPointerMove = useCallback((e) => {
    if (!hvacMode) return false;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const x = (e.clientX - rect.left - offset.x) / zoom;
    const y = (e.clientY - rect.top - offset.y) / zoom;

    // Update preview position for vent/equipment/fitting/stamp/label placement
    if (hvacTool === 'supply-vent' || hvacTool === 'return-vent' || hvacTool === 'equipment' ||
        hvacTool === 'stamp' || hvacTool === 'cfm-label' || hvacTool === 'duct-label') {
      setHvacPreviewPosition({ x, y });
    }

    // For fittings, check if we should snap to a duct endpoint
    if (hvacTool === 'elbow' || hvacTool === 'tee') {
      const snapPoint = findNearestDuctEndpoint(x, y);
      if (snapPoint) {
        // Snap preview to duct endpoint
        setHvacPreviewPosition({
          x: snapPoint.x,
          y: snapPoint.y,
          snapped: true,
          snapAngle: snapPoint.angle,
          snapDuctType: snapPoint.ductType,
          snapDuctSize: snapPoint.ductSize
        });
      } else {
        setHvacPreviewPosition({ x, y, snapped: false });
      }
    }

    // Handle dragging stamp or label
    if (draggingHvacItem) {
      const newX = x - draggingHvacItem.offset.x;
      const newY = y - draggingHvacItem.offset.y;

      if (draggingHvacItem.type === 'stamp') {
        const stamp = activeFloor?.hvacStamps?.find(s => s.id === draggingHvacItem.id);
        if (stamp) {
          handleUpdateStamp(draggingHvacItem.id, { position: { x: newX, y: newY } });
        }
      } else if (draggingHvacItem.type === 'label') {
        const label = activeFloor?.hvacLabels?.find(l => l.id === draggingHvacItem.id);
        if (label) {
          handleUpdateLabel(draggingHvacItem.id, { position: { x: newX, y: newY } });
        }
      }
      return true;
    }

    // Handle dragging duct endpoint
    if (draggingDuctPoint) {
      const { ductId, pointIndex } = draggingDuctPoint;
      handleUpdateDuct(ductId, {
        points: activeFloor.ducts
          .find(d => d.id === ductId)
          ?.points.map((pt, i) => i === pointIndex ? { x, y } : pt)
      });
      return true;
    }

    // Handle drawing new duct
    if (isDrawingDuct) {
      setDuctPreviewPoints(prev => {
        if (prev.length === 0) return [{ x, y }];
        // Update second point for preview
        return [prev[0], { x, y }];
      });
      return true;
    }

    return false;
  }, [hvacMode, hvacTool, isDrawingDuct, draggingDuctPoint, draggingHvacItem, zoom, offset, activeFloor, handleUpdateDuct, handleUpdateStamp, handleUpdateLabel, findNearestDuctEndpoint]);

  const handleHvacPointerUp = useCallback((e) => {
    if (!hvacMode) return false;

    // Handle end of dragging stamp or label
    if (draggingHvacItem) {
      setDraggingHvacItem(null);
      return true;
    }

    // Handle end of dragging duct point
    if (draggingDuctPoint) {
      setDraggingDuctPoint(null);
      return true;
    }

    // Handle drawing duct
    if (isDrawingDuct) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return false;

      const x = (e.clientX - rect.left - offset.x) / zoom;
      const y = (e.clientY - rect.top - offset.y) / zoom;

      // Check if we have a valid duct (at least some length)
      if (ductPreviewPoints.length >= 1) {
        const startPt = ductPreviewPoints[0];
        const dx = x - startPt.x;
        const dy = y - startPt.y;
        const length = Math.sqrt(dx * dx + dy * dy);

        if (length > 12) { // Minimum 12 inches (1 foot)
          const newDuct = handleAddDuct({
            type: ductType,
            ductShape: ductShape,
            size: ductSize,
            points: [startPt, { x, y }],
          });
          setSelectedDuctId(newDuct.id);
        }
      }

      setIsDrawingDuct(false);
      setDuctPreviewPoints([]);
      return true;
    }

    // For select tool, return false so endPan() can be called
    return false;
  }, [hvacMode, isDrawingDuct, draggingDuctPoint, draggingHvacItem, hvacTool, ductPreviewPoints, ductType, ductShape, ductSize, zoom, offset, handleAddDuct, activeFloor]);

  // Get currently selected item
  const selectedItem = selectedItems.length === 1 ? selectedItems[0] : null;

  // Auto-restore from localStorage on initial load
  useEffect(() => {
    if (hasRestoredAutoSave) return;

    const saved = loadAutoSave();
    if (saved && saved.data) {
      try {
        // A large background image was kept in IndexedDB - restore it once loaded
        const hasStoredImage = saved.data.includes(AUTOSAVE_IMAGE_MARKER);
        const result = importFromJSON(hasStoredImage ? saved.data.replace(`"${AUTOSAVE_IMAGE_MARKER}"`, 'null') : saved.data);
        if (result.success) {
          setProjectName(saved.projectName || 'floorplan');
          // Restore estimates if present
          if (result.estimates && result.estimates.length > 0) {
            setEstimates(result.estimates);
          }
          // Restore invoices if present
          if (result.invoices && result.invoices.length > 0) {
            setInvoices(result.invoices);
          }
          if (result.grading) setGrading({ ...DEFAULT_GRADING, ...result.grading });
          if (result.terrain === AUTOSAVE_TERRAIN_MARKER) {
            loadAutoSaveAsset('terrain').then(saved => setTerrain(saved || null));
          }
          if (result.siteModel) {
            loadAutoSaveAsset('siteModel').then(saved => setSiteModel(saved || null));
          }
          if (hasStoredImage) {
            loadAutoSaveAsset('background').then(dataUrl => {
              if (dataUrl) updatePdfLayer({ dataUrl });
              else removePdfLayer();
            });
          }
          console.log('Restored auto-saved project:', saved.projectName);
        }
      } catch (e) {
        console.warn('Failed to restore auto-save:', e);
      }
    }
    setHasRestoredAutoSave(true);
  }, [hasRestoredAutoSave, importFromJSON, updatePdfLayer, removePdfLayer]);

  // Auto-save to localStorage when floor plan changes
  useEffect(() => {
    if (!hasRestoredAutoSave) return; // Don't save until we've tried to restore

    const largeBackground = pdfLayer.dataUrl?.length > AUTOSAVE_INLINE_IMAGE_LIMIT;
    if (largeBackground) saveAutoSaveAsset('background', pdfLayer.dataUrl);
    if (terrain) saveAutoSaveAsset('terrain', terrain);
    if (siteModel) saveAutoSaveAsset('siteModel', siteModel);
    const data = exportToJSON({
      estimates: estimates.length > 0 ? estimates : undefined,
      invoices: invoices.length > 0 ? invoices : undefined,
      terrain: terrain ? AUTOSAVE_TERRAIN_MARKER : undefined,
      siteModel: siteModel ? { fileName: siteModel.fileName } : undefined,
      grading,
      ...(largeBackground && {
        pdfLayer: { ...pdfLayer, pdfDoc: null, pdfArrayBuffer: null, dataUrl: AUTOSAVE_IMAGE_MARKER },
      }),
    });
    saveAutoSave(data, projectName);
  }, [floors, pdfLayer, annotations, annotationSettings, estimates, invoices, terrain, siteModel, grading, projectName, hasRestoredAutoSave, exportToJSON]);

  // Format measurement based on units
  const formatMeasurementFn = useCallback((feet) => {
    return formatMeasurement(feet, units);
  }, [units]);

  // Fit view to content - calculates bounds from floor plan and zooms to fit
  const fitToContent = useCallback(() => {
    if (!activeFloor) return;

    const walls = activeFloor.walls || [];
    const furniture = activeFloor.furniture || [];
    const rooms = activeFloor.rooms || [];

    // Collect all points
    const points = [];

    // Add wall endpoints
    walls.forEach(wall => {
      points.push(wall.start);
      points.push(wall.end);
    });

    // Add furniture positions
    furniture.forEach(f => {
      points.push({ x: f.x, y: f.y });
      // Include furniture size in bounds
      const width = f.width || 50;
      const height = f.height || 50;
      points.push({ x: f.x + width, y: f.y + height });
    });

    // Add room points
    rooms.forEach(room => {
      if (room.points) {
        room.points.forEach(p => points.push(p));
      }
    });

    // If no content, reset to default
    if (points.length === 0) {
      resetView();
      return;
    }

    // Calculate bounds
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));

    // Add some padding
    const padding = 50;
    zoomToFit({
      minX: minX - padding,
      maxX: maxX + padding,
      minY: minY - padding,
      maxY: maxY + padding,
    });
  }, [activeFloor, zoomToFit, resetView]);

  // Handle delete selected - accepts items to avoid stale closure
  const deleteSelected = useCallback((itemsToDelete) => {
    if (!itemsToDelete || !itemsToDelete.length) return;

    itemsToDelete.forEach(({ type, item, id }) => {
      // Get the item id - may be in item.id or directly in id
      const itemId = item?.id || id;

      if (type === 'wall') {
        updateActiveFloor(f => ({
          ...f,
          walls: f.walls.filter(w => w.id !== itemId),
          doors: f.doors.filter(d => d.wallId !== itemId),
          windows: f.windows.filter(w => w.wallId !== itemId),
        }));
      } else if (type === 'door') {
        updateActiveFloor(f => ({ ...f, doors: f.doors.filter(d => d.id !== itemId) }));
      } else if (type === 'window') {
        updateActiveFloor(f => ({ ...f, windows: f.windows.filter(w => w.id !== itemId) }));
      } else if (type === 'furniture') {
        updateActiveFloor(f => ({ ...f, furniture: f.furniture.filter(furn => furn.id !== itemId) }));
      } else if (type === 'fireSafety') {
        updateActiveFloor(f => ({ ...f, fireSafety: (f.fireSafety || []).filter(fs => fs.id !== itemId) }));
      } else if (type === 'post') {
        updateActiveFloor(f => ({ ...f, posts: (f.posts || []).filter(p => p.id !== itemId) }));
      } else if (type === 'panel') {
        updateActiveFloor(f => ({ ...f, panels: (f.panels || []).filter(p => p.id !== itemId) }));
      } else if (type === 'room') {
        updateActiveFloor(f => ({ ...f, rooms: (f.rooms || []).filter(r => r.id !== itemId) }));
      } else if (type === 'roof') {
        updateActiveFloor(f => ({ ...f, roofs: (f.roofs || []).filter(r => r.id !== itemId) }));
      } else if (type === 'dimension') {
        updateActiveFloor(f => ({ ...f, dimensions: (f.dimensions || []).filter(d => d.id !== itemId) }));
      } else if (type === 'line') {
        updateActiveFloor(f => ({ ...f, lines: (f.lines || []).filter(l => l.id !== itemId) }));
      } else if (type === 'polyline') {
        updateActiveFloor(f => ({ ...f, polylines: (f.polylines || []).filter(p => p.id !== itemId) }));
      } else if (type === 'text') {
        updateActiveFloor(f => ({ ...f, texts: (f.texts || []).filter(t => t.id !== itemId) }));
      } else if (type === 'hatch') {
        updateActiveFloor(f => ({ ...f, hatches: (f.hatches || []).filter(h => h.id !== itemId) }));
      }
    });

    setSelectedItems([]);
  }, [updateActiveFloor, setSelectedItems]);

  // Close panels when clicking outside
  const handleBackgroundClick = useCallback(() => {
    setActivePanel(null);
    setShowMobileMenu(false);
    setShowFloorSelector(false);
  }, []);

  // Handle wheel event
  const onWheel = useCallback((e) => {
    handleWheel(e);
  }, [handleWheel]);

  // File operations
  const handleNewFile = useCallback(() => {
    // Check project limit for free users
    const projectCount = getRecentProjects().length;
    if (!canCreateProject(projectCount)) {
      requirePro(PRO_FEATURES.UNLIMITED_PROJECTS, 'Unlimited Projects');
      return;
    }

    if (confirm('Create a new project? Unsaved changes will be lost.')) {
      newFile();
      setTerrain(null);
      setSiteModel(null);
      setGrading(DEFAULT_GRADING);
      setProjectName('floorplan');
      clearAutoSave();
    }
  }, [newFile, canCreateProject, requirePro]);

  // Handle template selection
  const handleSelectTemplate = useCallback(({ template, scaleFactor }) => {
    loadFromTemplate(template, scaleFactor);
    setTerrain(null);
    setSiteModel(null);
    setGrading(DEFAULT_GRADING);
    setProjectName(template.name.toLowerCase().replace(/\s+/g, '-'));
    clearAutoSave();
    setShowTemplateSelector(false);
    // Reset view to fit the new floor plan
    setScale(0.8);
    setOffset({ x: 100, y: 50 });
  }, [loadFromTemplate]);

  const handleSaveFile = useCallback(() => {
    // Show the save dialog to let user enter a filename
    setShowSaveDialog(true);
  }, []);

  const handleSaveWithName = useCallback((name) => {
    const data = exportToJSON({
      estimates: estimates.length > 0 ? estimates : undefined,
      invoices: invoices.length > 0 ? invoices : undefined,
      terrain: serializeTerrain(terrain) || undefined,
      siteModel: siteModel ? { fileName: siteModel.fileName } : undefined,
      grading,
    });
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setProjectName(name);
    setShowSaveDialog(false);
    // Add to recent projects
    addToRecentProjects(name, data);
  }, [exportToJSON, estimates, invoices, terrain, siteModel, grading]);

  const handleLoadFile = useCallback((jsonData, loadedProjectName = null) => {
    const result = importFromJSON(jsonData);
    if (result.success) {
      if (loadedProjectName) {
        setProjectName(loadedProjectName);
      }
      // Restore estimates if present
      if (result.estimates && result.estimates.length > 0) {
        setEstimates(result.estimates);
      } else {
        setEstimates([]);
      }
      // Restore invoices if present
      if (result.invoices && result.invoices.length > 0) {
        setInvoices(result.invoices);
      } else {
        setInvoices([]);
      }
      setTerrain(deserializeTerrain(result.terrain));
      setSiteModel(prev => (prev && result.siteModel?.fileName === prev.fileName ? prev : null));
      setMissingSiteModel(result.siteModel?.fileName || null);
      setGrading({ ...DEFAULT_GRADING, ...result.grading });
      setShowRecentProjects(false);
    } else {
      alert('Failed to load project. Invalid file format.');
    }
  }, [importFromJSON]);

  const handleLoadFromRecent = useCallback((jsonData, recentProjectName) => {
    handleLoadFile(jsonData, recentProjectName);
  }, [handleLoadFile]);

  // Site grading: turn a 3Dsurvey mesh into an elevation grid in the image's coordinates
  const handleLoadTerrain = useCallback(async (file) => {
    const geo = pdfLayer?.geo;
    const buffer = await file.arrayBuffer();
    // Let the panel paint its "processing" state before the heavy work
    await new Promise(resolve => setTimeout(resolve, 30));
    const grid = buildTerrainGrid(parsePly(buffer), geo.units === 'm' ? 0.3048 : 1);

    const imageRight = geo.originX + pdfLayer.width / geo.unitsToPlanPx;
    const imageBottom = geo.originY - pdfLayer.height / geo.unitsToPlanPx;
    const gridRight = grid.originX + grid.cols * grid.cellSize;
    const gridBottom = grid.originY - grid.rows * grid.cellSize;
    if (grid.originX > imageRight || gridRight < geo.originX || grid.originY < imageBottom || gridBottom > geo.originY) {
      throw new Error("The elevation data doesn't overlap the aerial image. Check that both were exported in the same coordinate system.");
    }
    setTerrain({ ...grid, units: geo.units, fileName: file.name });
  }, [pdfLayer]);

  const handleLoadSiteModel = useCallback(async (file) => {
    const buffer = await file.arrayBuffer();
    const magic = new TextDecoder().decode(new Uint8Array(buffer, 0, 4));
    if (magic !== 'glTF') {
      throw new Error('Choose a .glb file made with the site-model converter from your 3Dsurvey OBJ export.');
    }
    setSiteModel({ buffer, fileName: file.name, size: file.size });
    setMissingSiteModel(null);
  }, []);

  const arenaRoom = activeFloor?.rooms?.find(r => r.id === grading.roomId && r.points?.length >= 3) || null;
  const arenaShape = arenaRoom ? describeArena(arenaRoom) : null;

  const handleAddArena = useCallback(() => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const center = rect
      ? screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2)
      : screenToWorld(window.innerWidth / 2, window.innerHeight / 2);
    const id = addRoom({
      name: 'Arena',
      ...arenaRoomFields({ center, widthFt: 100, lengthFt: 200, angleDeg: 0 }),
      color: 'rgba(255, 220, 120, 0.08)',
    });
    setGrading(prev => ({ ...prev, roomId: id }));
  }, [addRoom, screenToWorld]);

  const handleResizeArena = useCallback((changes) => {
    if (!arenaRoom || !arenaShape) return;
    const reshaped = ['widthFt', 'lengthFt', 'cornerRadiusFt', 'alley'].some(k => k in changes);
    const presetId = changes.presetId ?? (reshaped ? null : arenaShape.presetId);
    updateRoom(arenaRoom.id, arenaRoomFields({ ...arenaShape, ...changes, presetId }));
  }, [arenaRoom, arenaShape, updateRoom]);

  const panelToolChoice = (() => {
    const product = findPanelProduct(panelChoice.productId);
    const option = product?.options.find(o => o.label === panelChoice.optionLabel)
      || product?.options.find(o => o.kind === 'panel');
    return product && option ? { product, option } : null;
  })();
  const {
    panelPreview,
    panelRunActive,
    handlePanelPointerDown,
    handlePanelPointerMove,
    cancelPanelRun,
  } = usePanelTool({
    active: tool === 'panel',
    choice: panelToolChoice,
    screenToWorld,
    scale: zoom,
    activeFloor,
    updateActiveFloor,
    setSelectedItems,
  });

  // Leaving the Panel tool ends any run in progress; choosing it opens its settings
  useEffect(() => {
    if (tool === 'panel') setActivePanel('arena-panels');
    else cancelPanelRun();
  }, [tool, cancelPanelRun]);

  // Recompute earthwork shortly after the outline or options stop changing
  useEffect(() => {
    if (!terrain || !pdfLayer?.geo || !arenaRoom) {
      setGradingResult(null);
      return;
    }
    const timer = setTimeout(() => {
      const pad = collectPadCells(terrain, pdfLayer, arenaRoom.points);
      if (!pad) {
        setGradingResult(null);
        return;
      }
      const result = gradePad(pad, grading);
      const overlay = grading.showOverlay ? buildCutFillOverlay(terrain, pdfLayer, pad, result) : null;
      setGradingResult({ result, overlay });
    }, 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terrain, pdfLayer?.geo, pdfLayer?.position, arenaRoom?.points, grading]);

  const handleExport = useCallback(async (options) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      alert('No canvas to export');
      return;
    }

    try {
      if (options.format === 'pdf') {
        await exportToPDF(canvas, options);
      } else if (options.format === 'png') {
        exportToPNG(canvas, { projectName: options.projectName, scale: options.imageScale });
      } else if (options.format === 'jpeg') {
        exportToJPEG(canvas, { projectName: options.projectName, quality: options.jpegQuality });
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  }, [canvasRef]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Handle PDF scale calibration with automatic zoom to fit
  const handleCalibrateScale = useCallback((scaleRatio) => {
    calibratePdfScale(scaleRatio);

    // After calibration, the PDF will be much larger - zoom to fit it
    // We need to wait for state to update, then zoom to fit
    setTimeout(() => {
      // Calculate new PDF bounds based on the scale ratio
      const FLOOR_PLAN_PX_PER_INCH = 40 / 12;
      const PDF_DPI = 72;
      const scaleFactor = (scaleRatio * FLOOR_PLAN_PX_PER_INCH) / PDF_DPI;

      const originalWidth = pdfLayer.originalWidth || pdfLayer.width;
      const originalHeight = pdfLayer.originalHeight || pdfLayer.height;

      const newWidth = originalWidth * scaleFactor;
      const newHeight = originalHeight * scaleFactor;

      const pdfBounds = {
        minX: pdfLayer.position?.x || 0,
        minY: pdfLayer.position?.y || 0,
        maxX: (pdfLayer.position?.x || 0) + newWidth,
        maxY: (pdfLayer.position?.y || 0) + newHeight,
      };

      zoomToFit(pdfBounds, 100);
    }, 100);
  }, [calibratePdfScale, pdfLayer, zoomToFit]);

  // PDF drag handlers
  const handlePdfPointerDown = useCallback((e) => {
    if (!isMovingPdf || !pdfLayer?.dataUrl) return false;

    // Get world coordinates
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const worldX = (e.clientX - rect.left - offset.x) / zoom;
    const worldY = (e.clientY - rect.top - offset.y) / zoom;

    // Check if click is within PDF bounds
    const pdfX = pdfLayer.position?.x || 0;
    const pdfY = pdfLayer.position?.y || 0;
    const pdfWidth = pdfLayer.width || 0;
    const pdfHeight = pdfLayer.height || 0;

    if (worldX >= pdfX && worldX <= pdfX + pdfWidth &&
        worldY >= pdfY && worldY <= pdfY + pdfHeight) {
      setPdfDragStart({
        mouseX: worldX,
        mouseY: worldY,
        pdfX: pdfX,
        pdfY: pdfY,
      });
      return true;
    }
    return false;
  }, [isMovingPdf, pdfLayer, zoom, offset]);

  const handlePdfPointerMove = useCallback((e) => {
    if (!pdfDragStart || !isMovingPdf) return false;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const worldX = (e.clientX - rect.left - offset.x) / zoom;
    const worldY = (e.clientY - rect.top - offset.y) / zoom;

    const dx = worldX - pdfDragStart.mouseX;
    const dy = worldY - pdfDragStart.mouseY;

    updatePdfLayer({
      position: {
        x: pdfDragStart.pdfX + dx,
        y: pdfDragStart.pdfY + dy,
      }
    });
    return true;
  }, [pdfDragStart, isMovingPdf, zoom, offset, updatePdfLayer]);

  const handlePdfPointerUp = useCallback(() => {
    if (pdfDragStart) {
      setPdfDragStart(null);
      return true;
    }
    return false;
  }, [pdfDragStart]);

  // Ref to track drawing measurement for click handler (avoids stale closure)
  const drawingMeasurementRef = useRef(drawingMeasurement);

  // Helper to update both state and ref synchronously
  const updateDrawingMeasurement = useCallback((value) => {
    drawingMeasurementRef.current = value;
    setDrawingMeasurement(value);
  }, []);

  // PDF Measurement handlers
  const handleMeasurementPointerDown = useCallback((e) => {
    // Allow measurements even without calibration (uses default scale)
    if (!activeMeasureTool) return false;

    // Ignore right clicks and check for primary button
    if (e.button !== 0) return false;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const worldX = (e.clientX - rect.left - offset.x) / zoom;
    const worldY = (e.clientY - rect.top - offset.y) / zoom;
    const clickPoint = { x: worldX, y: worldY };

    const currentDrawing = drawingMeasurementRef.current;

    if (activeMeasureTool === 'linear') {
      if (!currentDrawing) {
        // Start new linear measurement
        const newDrawing = {
          type: 'linear',
          points: [clickPoint],
          previewPoint: clickPoint,
        };
        updateDrawingMeasurement(newDrawing);
      } else if (currentDrawing.points.length === 1) {
        // Complete linear measurement
        const points = [currentDrawing.points[0], clickPoint];
        const dx = points[1].x - points[0].x;
        const dy = points[1].y - points[0].y;
        const pixelLength = Math.sqrt(dx * dx + dy * dy);
        const length = pixelsToInches(pixelLength);

        addPdfMeasurement({
          type: 'linear',
          points,
          length,
        });
        updateDrawingMeasurement(null);
      }
      return true;
    }

    if (activeMeasureTool === 'area') {
      if (!currentDrawing) {
        // Start new area measurement
        const newDrawing = {
          type: 'area',
          points: [clickPoint],
          previewPoint: clickPoint,
        };
        updateDrawingMeasurement(newDrawing);
      } else {
        const firstPoint = currentDrawing.points[0];
        const dist = Math.sqrt(
          Math.pow(clickPoint.x - firstPoint.x, 2) +
          Math.pow(clickPoint.y - firstPoint.y, 2)
        );
        const closeThreshold = 20 / zoom; // 20 screen pixels

        if (currentDrawing.points.length >= 3 && dist < closeThreshold) {
          // Close the polygon
          const points = currentDrawing.points;
          const pixelArea = calculatePolygonArea(points);
          const area = squarePixelsToSquareInches(Math.abs(pixelArea));

          addPdfMeasurement({
            type: 'area',
            points,
            area,
          });
          updateDrawingMeasurement(null);
        } else {
          // Add another point
          const newDrawing = {
            ...currentDrawing,
            points: [...currentDrawing.points, clickPoint],
          };
          updateDrawingMeasurement(newDrawing);
        }
      }
      return true;
    }

    return false;
  }, [activeMeasureTool, zoom, offset, addPdfMeasurement, updateDrawingMeasurement]);

  const handleMeasurementPointerMove = useCallback((e) => {
    const currentDrawing = drawingMeasurementRef.current;
    if (!activeMeasureTool || !currentDrawing) return false;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const worldX = (e.clientX - rect.left - offset.x) / zoom;
    const worldY = (e.clientY - rect.top - offset.y) / zoom;

    const updated = {
      ...currentDrawing,
      previewPoint: { x: worldX, y: worldY },
    };
    updateDrawingMeasurement(updated);
    return true;
  }, [activeMeasureTool, zoom, offset, updateDrawingMeasurement]);

  const handleMeasurementClick = useCallback((measurementId, isShiftKey) => {
    if (isShiftKey) {
      // Toggle selection
      setSelectedMeasurementIds(prev =>
        prev.includes(measurementId)
          ? prev.filter(id => id !== measurementId)
          : [...prev, measurementId]
      );
    } else {
      // Single select
      setSelectedMeasurementIds([measurementId]);
    }
    // Show totals panel when measurements are selected
    setShowMeasurementTotals(true);
  }, []);

  // Handle starting to drag a measurement grip
  const handleMeasurementGripStart = useCallback((measurementId, pointIndex, e) => {
    setDraggingMeasurementGrip({ measurementId, pointIndex });
    // Capture pointer to ensure we get all pointer events even if cursor leaves the element
    if (e?.target?.setPointerCapture && e?.pointerId !== undefined) {
      try {
        e.target.setPointerCapture(e.pointerId);
      } catch (err) {
        // Ignore capture errors
      }
    }
  }, []);

  // Handle dragging a measurement grip
  const handleMeasurementGripMove = useCallback((e) => {
    if (!draggingMeasurementGrip) return false;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const worldX = (e.clientX - rect.left - offset.x) / zoom;
    const worldY = (e.clientY - rect.top - offset.y) / zoom;

    const measurement = pdfMeasurements.find(m => m.id === draggingMeasurementGrip.measurementId);
    if (!measurement) return false;

    // Update the point position
    const newPoints = [...measurement.points];
    newPoints[draggingMeasurementGrip.pointIndex] = { x: worldX, y: worldY };

    // Recalculate length or area
    if (measurement.type === 'linear') {
      const dx = newPoints[1].x - newPoints[0].x;
      const dy = newPoints[1].y - newPoints[0].y;
      const pixelLength = Math.sqrt(dx * dx + dy * dy);
      const length = pixelsToInches(pixelLength);
      updatePdfMeasurement(draggingMeasurementGrip.measurementId, { points: newPoints, length });
    } else if (measurement.type === 'area') {
      const pixelArea = calculatePolygonArea(newPoints);
      const area = squarePixelsToSquareInches(Math.abs(pixelArea));
      updatePdfMeasurement(draggingMeasurementGrip.measurementId, { points: newPoints, area });
    }

    return true;
  }, [draggingMeasurementGrip, pdfMeasurements, zoom, offset, updatePdfMeasurement]);

  // Handle releasing a measurement grip
  const handleMeasurementGripEnd = useCallback(() => {
    setDraggingMeasurementGrip(null);
  }, []);

  const handleDeleteSelectedMeasurements = useCallback(() => {
    selectedMeasurementIds.forEach(id => removePdfMeasurement(id));
    setSelectedMeasurementIds([]);
  }, [selectedMeasurementIds, removePdfMeasurement]);

  // Brief confirmation shown in the status bar after a copy or paste
  const showClipboardMessage = useCallback((text) => {
    setClipboardMessage(text);
    if (clipboardMessageTimer.current) clearTimeout(clipboardMessageTimer.current);
    clipboardMessageTimer.current = setTimeout(() => setClipboardMessage(null), 2500);
  }, []);

  useEffect(() => () => {
    if (clipboardMessageTimer.current) clearTimeout(clipboardMessageTimer.current);
  }, []);

  // Every selectable item on the active floor, in the { type, item } shape the
  // rest of the app expects.
  const selectAllOnFloor = useCallback(() => {
    if (!activeFloor) return;
    const groups = [
      ['wall', activeFloor.walls],
      ['door', activeFloor.doors],
      ['window', activeFloor.windows],
      ['furniture', activeFloor.furniture],
      ['fireSafety', activeFloor.fireSafety],
      ['post', activeFloor.posts],
      ['panel', activeFloor.panels],
      ['room', activeFloor.rooms],
      ['roof', activeFloor.roofs],
      ['dimension', activeFloor.dimensions],
      ['line', activeFloor.lines],
      ['text', activeFloor.texts],
      ['polyline', activeFloor.polylines],
      ['hatch', activeFloor.hatches],
    ];
    const all = [];
    groups.forEach(([type, items]) => (items || []).forEach(item => all.push({ type, item })));
    setSelectedItems(all);
  }, [activeFloor, setSelectedItems]);

  const handleCopy = useCallback(() => {
    const count = copySelected();
    if (count > 0) {
      showClipboardMessage(`Copied ${count} item${count === 1 ? '' : 's'}`);
    }
    return count;
  }, [copySelected, showClipboardMessage]);

  // Drop the clipboard at a specific world point and report what happened.
  const pasteAt = useCallback((target) => {
    const { pasted, skipped } = paste(target, { wallAtTarget: findNearestWall(target) });

    if (pasted > 0) {
      showClipboardMessage(
        skipped > 0
          ? `Pasted ${pasted}, skipped ${skipped} (not on a wall)`
          : `Pasted ${pasted} item${pasted === 1 ? '' : 's'}`
      );
    } else if (skipped > 0) {
      showClipboardMessage(canHover
        ? 'Point at a wall to paste a door or window'
        : 'Tap on a wall to paste a door or window');
    }
    return pasted;
  }, [paste, findNearestWall, showClipboardMessage, canHover]);

  const handlePaste = useCallback(() => {
    if (!hasClipboard) return;

    // Without hover there is no "where the pointer is", so ask for a tap
    // instead - handled in wrappedPointerDown. Covers tablets too, which are
    // wide enough to miss the isMobile breakpoint but still have no cursor.
    if (!canHover) {
      setPendingPaste(true);
      return;
    }

    // Paste where the pointer is. If it has never been over the canvas (a
    // keyboard-only paste), fall back to the middle of the current view.
    const pointer = lastPointerRef.current;
    const rect = canvasRef.current?.getBoundingClientRect();
    let target;
    if (pointer) {
      target = screenToWorld(pointer.x, pointer.y);
    } else if (rect) {
      target = screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2);
    } else {
      target = { x: 0, y: 0 };
    }

    pasteAt(target);
  }, [hasClipboard, canHover, screenToWorld, pasteAt]);

  // Handle keyboard shortcuts (must be after handleDeleteSelectedMeasurements is defined)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      const withModifier = e.ctrlKey || e.metaKey;

      // Ignore if in an input field - except for Ctrl/Cmd shortcuts when the
      // field was auto-focused rather than clicked into, which is the case
      // right after selecting an object on the canvas.
      const inFormField = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
      if (inFormField && !(withModifier && !clickedIntoFieldRef.current)) return;

      // Tool shortcuts - bare keys only, so Ctrl+V pastes instead of also
      // switching to the select tool.
      if (!withModifier) {
        if (key === 'v' || key === 'escape') setTool('select');
        if (key === 'w') setTool('wall');
        if (key === 'd') setTool('door');
        if (key === 'n') setTool('window');
        if (key === 'r') setTool('room');
        if (key === 'f') setTool('roof');
        if (key === 'p') setTool('pan');
      }

      // Undo/Redo
      if ((e.ctrlKey || e.metaKey) && key === 'z') {
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        e.preventDefault();
      }

      // Copy - only claim the shortcut when something is selected, so
      // Ctrl+C still copies page text otherwise.
      if ((e.ctrlKey || e.metaKey) && key === 'c' && selectedItems.length > 0) {
        handleCopy();
        e.preventDefault();
      }

      // Paste at the cursor
      if ((e.ctrlKey || e.metaKey) && key === 'v' && hasClipboard) {
        handlePaste();
        e.preventDefault();
      }

      // Rotate vent/equipment with R key
      if (key === 'r' && hvacMode) {
        // Rotate selected vent
        if (selectedVentId) {
          const vent = activeFloor?.vents?.find(v => v.id === selectedVentId);
          if (vent) {
            handleUpdateVent({ rotation: ((vent.rotation || 0) + 90) % 360 });
            e.preventDefault();
            return;
          }
        }
        // Rotate selected equipment
        if (selectedEquipmentId) {
          const equip = activeFloor?.equipment?.find(eq => eq.id === selectedEquipmentId);
          if (equip) {
            handleUpdateEquipment({ rotation: ((equip.rotation || 0) + 90) % 360 });
            e.preventDefault();
            return;
          }
        }
        // Rotate selected fitting
        if (selectedFittingId) {
          const fitting = activeFloor?.fittings?.find(f => f.id === selectedFittingId);
          if (fitting) {
            handleUpdateFitting(selectedFittingId, { rotation: ((fitting.rotation || 0) + 90) % 360 });
            e.preventDefault();
            return;
          }
        }
        // Rotate selected stamp
        if (selectedHvacStamp) {
          const stamp = activeFloor?.hvacStamps?.find(s => s.id === selectedHvacStamp);
          if (stamp) {
            handleUpdateStamp(selectedHvacStamp, { rotation: ((stamp.rotation || 0) + 90) % 360 });
            e.preventDefault();
            return;
          }
        }
        // Rotate preview while placing
        if (hvacTool === 'supply-vent' || hvacTool === 'return-vent') {
          setVentRotation(prev => (prev + 90) % 360);
          e.preventDefault();
        } else if (hvacTool === 'equipment') {
          setEquipmentRotation(prev => (prev + 90) % 360);
          e.preventDefault();
        } else if (hvacTool === 'elbow' || hvacTool === 'tee') {
          setFittingRotation(prev => (prev + 90) % 360);
          e.preventDefault();
        }
      }

      // Delete
      if (key === 'delete' || key === 'backspace') {
        // Delete selected HVAC duct if any
        if (selectedDuctId) {
          handleDeleteDuct(selectedDuctId);
          e.preventDefault();
        }
        // Delete selected HVAC vent if any
        else if (selectedVentId) {
          handleDeleteVent(selectedVentId);
          e.preventDefault();
        }
        // Delete selected HVAC equipment if any
        else if (selectedEquipmentId) {
          handleDeleteEquipment(selectedEquipmentId);
          e.preventDefault();
        }
        // Delete selected HVAC fitting if any
        else if (selectedFittingId) {
          handleDeleteFitting(selectedFittingId);
          e.preventDefault();
        }
        // Delete selected HVAC stamp if any
        else if (selectedHvacStamp) {
          handleDeleteStamp(selectedHvacStamp);
          e.preventDefault();
        }
        // Delete selected HVAC label if any
        else if (selectedHvacLabel) {
          handleDeleteLabel(selectedHvacLabel);
          e.preventDefault();
        }
        // Delete selected PDF measurements if any
        else if (selectedMeasurementIds.length > 0) {
          handleDeleteSelectedMeasurements();
          e.preventDefault();
        }
        // Delete selected annotations if any
        else if (selectedAnnotationIds.length > 0) {
          deleteSelectedAnnotations();
          e.preventDefault();
        }
        // Delete selected floor plan items if any
        else if (selectedItems.length > 0) {
          deleteSelected(selectedItems);
          e.preventDefault();
        }
      }

      // Resize selected stamp with + and - keys
      if ((key === '+' || key === '=' || key === 'add') && selectedAnnotationIds.length === 1) {
        resizeSelectedStamp(0.1);
        e.preventDefault();
      }
      if ((key === '-' || key === 'subtract') && selectedAnnotationIds.length === 1) {
        resizeSelectedStamp(-0.1);
        e.preventDefault();
      }

      // Save
      if ((e.ctrlKey || e.metaKey) && key === 's') {
        e.preventDefault();
        setShowSaveDialog(true);
      }

      // Escape - cancel drawing or deselect
      if (key === 'escape') {
        if (drawingAnnotation && drawingAnnotation.type === 'cloud') {
          cancelCloudDrawing();
          e.preventDefault();
        } else if (drawingMeasurement) {
          setDrawingMeasurement(null);
          e.preventDefault();
        } else if (activeMeasureTool) {
          setActiveMeasureTool(null);
          e.preventDefault();
        } else if (polylinePoints.length > 0) {
          cancelPolyline();
          e.preventDefault();
        } else if (hatchPoints.length > 0) {
          cancelHatch();
          e.preventDefault();
        } else if (roomPoints.length > 0) {
          cancelRoom();
          e.preventDefault();
        } else if (panelRunActive) {
          cancelPanelRun();
          e.preventDefault();
        } else if (selectedMeasurementIds.length > 0) {
          setSelectedMeasurementIds([]);
          e.preventDefault();
        } else if (selectedItems.length > 0) {
          setSelectedItems([]);
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, selectedItems, deleteSelected, selectedAnnotationIds, deleteSelectedAnnotations, resizeSelectedStamp, selectedMeasurementIds, handleDeleteSelectedMeasurements, drawingMeasurement, drawingAnnotation, cancelCloudDrawing, activeMeasureTool, polylinePoints, cancelPolyline, hatchPoints, cancelHatch, roomPoints, cancelRoom, panelRunActive, cancelPanelRun, setSelectedItems, selectedDuctId, handleDeleteDuct, selectedVentId, handleDeleteVent, selectedEquipmentId, handleDeleteEquipment, selectedFittingId, handleDeleteFitting, handleUpdateFitting, hvacMode, hvacTool, activeFloor, selectedHvacStamp, handleDeleteStamp, handleUpdateStamp, selectedHvacLabel, handleDeleteLabel, handleCopy, handlePaste, hasClipboard]);

  // Wrapped pointer handlers that check PDF move mode first, then measurement mode, then HVAC
  const fenceRoomId = panelFence.roomId || grading.roomId;
  // Site work (drone ground or arena panels) gets the full 3D site view instead of the house preview
  const hasSite3D = !!(terrain && pdfLayer?.geo) || !!activeFloor?.panels?.length;
  const handleFenceOutline = useCallback((fieldsOverride = null) => {
    const room = activeFloor?.rooms?.find(r => r.id === fenceRoomId);
    const product = findPanelProduct(panelChoice.productId);
    const panelOption = product?.options.find(o => o.label === panelFence.panelLabel)
      || product?.options.find(o => o.kind === 'panel');
    if (!room?.points || !panelOption) return;
    const gates = panelFence.gates
      .map(g => ({ side: g.side, option: product.options.find(o => o.label === g.label) }))
      .filter(g => g.option);
    // An arena shape is rebuilt so each corner chord is one panel long
    // fieldsOverride: { points, arena } just set on the room, not yet in state
    let points = fieldsOverride?.points || room.points;
    let shape = describeArena({ ...room, ...(fieldsOverride || {}) });
    if (shape && shape.cornerRadiusFt > 0 && !fieldsOverride) {
      shape = fitCornersToPanels(shape, panelOption.lengthFt);
      const fields = arenaRoomFields(shape);
      points = fields.points;
      updateRoom(room.id, fields);
    }
    const result = fenceOutline(points, product, panelOption, gates, room.id);
    let alleyPanels = [];
    const alleyRun = shape && arenaAlleyRun(shape);
    if (alleyRun) {
      // Gates across both ends of the alley when the line sells one that fits
      const alleyWidth = shape.alley.widthFt;
      const capGate = product.options
        .filter(o => o.kind === 'gate' && o.lengthFt <= alleyWidth + 0.5)
        .sort((a, b) => b.lengthFt - a.lengthFt)[0];
      const capGates = capGate ? [{ side: 0, option: capGate }, { side: 2, option: capGate }] : [];
      const alley = fenceOutline(alleyRun, product, panelOption, capGates, room.id, { closed: false });
      alleyPanels = alley.panels;
      result.sides.push(...alley.sides.map(s => ({ ...s, label: `Alley ${['end', 'side', 'end'][s.side]} (${Math.round(s.lengthFt)} ft)` })));
    }
    updateActiveFloor(f => ({
      ...f,
      panels: [...(f.panels || []).filter(p => p.fenceId !== room.id), ...result.panels, ...alleyPanels],
    }));
    setFenceReport(result.sides);
  }, [activeFloor?.rooms, fenceRoomId, panelFence, panelChoice.productId, updateActiveFloor, updateRoom]);

  const fenceRoom = activeFloor?.rooms?.find(r => r.id === fenceRoomId && describeArena(r)) || null;
  const handleFitOutline = useCallback(() => {
    const shape = fenceRoom && describeArena(fenceRoom);
    const option = findPanelProduct(panelChoice.productId)?.options.find(o => o.label === panelFence.panelLabel);
    if (!shape || !option) return;
    // Try whole-panel sizes near the current one (with and without the gate
    // lengths added) and keep the one where panels fit every side best
    const product = findPanelProduct(panelChoice.productId);
    const L = option.lengthFt;
    const fitted = fitCornersToPanels(shape, L);
    // Straight sides are what remains after the two rounded corners
    const corners = 2 * (fitted.cornerRadiusFt || 0);
    const gates = panelFence.gates
      .map(g => ({ side: g.side, option: product.options.find(o => o.label === g.label) }))
      .filter(g => g.option);
    const candidates = (ft) => {
      const base = Math.round((ft - corners) / L);
      const sizes = new Set();
      for (let n = Math.max(1, base - 1); n <= base + 1; n++) {
        sizes.add(n * L + corners);
        gates.forEach(g => sizes.add(n * L + corners + g.option.lengthFt));
      }
      return [...sizes];
    };
    let best = null;
    candidates(shape.widthFt).forEach(w => candidates(shape.lengthFt).forEach(l => {
      const points = arenaRoomFields({ ...fitted, widthFt: w, lengthFt: l }).points;
      const { sides } = fenceOutline(points, product, option, gates);
      const score = sides.reduce((s, side) => s + Math.abs(side.mismatchFt), 0)
        + 0.01 * (Math.abs(w - shape.widthFt) + Math.abs(l - shape.lengthFt));
      if (!best || score < best.score) best = { score, w, l };
    }));
    const fields = arenaRoomFields({ ...fitted, widthFt: best.w, lengthFt: best.l });
    updateRoom(fenceRoom.id, fields);
    handleFenceOutline(fields);
  }, [fenceRoom, panelChoice.productId, panelFence, updateRoom, handleFenceOutline]);

  // Place (or re-place) the Priefert roping-end / stripping-end equipment on the arena
  // Works on any outline: a reshaped one is laid out on its best-fitting rectangle.
  // `flipped` puts the roping end at the other end of the arena.
  const equipmentRoom = activeFloor?.rooms?.find(r => r.id === fenceRoomId && r.points?.length >= 3) || null;
  const handleArenaSetup = useCallback((setup, flipped = false) => {
    if (!equipmentRoom) return;
    let shape = describeArena(equipmentRoom) || approximateArenaFrame(equipmentRoom.points);
    if (!shape) return;
    if (flipped) {
      shape = {
        ...shape,
        angleDeg: shape.angleDeg + 180,
        alley: shape.alley && { ...shape.alley, side: shape.alley.side === 'left' ? 'right' : 'left' },
      };
    }
    const items = (setup === 'roping' ? ropingEndLayout : strippingEndLayout)(shape, equipmentRoom.id);
    updateActiveFloor(f => ({
      ...f,
      furniture: [
        ...(f.furniture || []).filter(fu => !(fu.arenaId === equipmentRoom.id && fu.arenaSetup === setup)),
        ...items,
      ],
    }));
  }, [equipmentRoom, updateActiveFloor]);

  const wrappedPointerDown = useCallback((e) => {
    // Touch paste: this tap chooses the spot, so it must not reach the active
    // tool and start drawing.
    if (pendingPaste) {
      e.preventDefault?.();
      e.stopPropagation?.();
      const point = e.clientX !== undefined
        ? { x: e.clientX, y: e.clientY }
        : { x: e.touches?.[0]?.clientX, y: e.touches?.[0]?.clientY };
      if (point.x !== undefined) pasteAt(screenToWorld(point.x, point.y));
      setPendingPaste(false);
      return;
    }

    if (handlePdfPointerDown(e)) return;
    if (handlePanelPointerDown(e)) return;
    if (handleMeasurementPointerDown(e)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (hvacMode && handleHvacPointerDown(e)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (hvacMode) {
      // In HVAC mode but didn't hit any HVAC item - start panning
      // Don't fall through to regular handler which would select/drag regular items
      startPan(e.clientX, e.clientY);
      return;
    }
    if (structuralMode && handleStructuralPointerDown(e)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (structuralMode) {
      startPan(e.clientX, e.clientY);
      return;
    }
    if (annotationMode && tool.startsWith('annotation-')) {
      handleAnnotationPointerDown(e, tool);
    } else {
      handlePointerDown(e);
    }
  }, [handlePdfPointerDown, handlePanelPointerDown, handleMeasurementPointerDown, hvacMode, handleHvacPointerDown, structuralMode, handleStructuralPointerDown, startPan, annotationMode, tool, handleAnnotationPointerDown, handlePointerDown, pendingPaste, pasteAt, screenToWorld]);

  const wrappedPointerMove = useCallback((e) => {
    // Recorded before any of the mode handlers can return early, so paste
    // knows where the cursor is no matter which mode the app is in.
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
    cursorListenerRef.current?.(e.clientX, e.clientY);
    handlePanelPointerMove(e);

    if (handlePdfPointerMove(e)) return;
    if (handleMeasurementGripMove(e)) return;
    if (handleMeasurementPointerMove(e)) return;
    if (hvacMode && handleHvacPointerMove(e)) return;
    if (hvacMode) {
      // In HVAC mode but HVAC didn't handle move - update panning
      updatePan(e.clientX, e.clientY);
      return;
    }
    if (structuralMode && handleStructuralPointerMove(e)) return;
    if (structuralMode) {
      updatePan(e.clientX, e.clientY);
      return;
    }
    if (annotationMode && tool.startsWith('annotation-')) {
      handleAnnotationPointerMove(e, tool);
    } else {
      handlePointerMove(e);
    }
  }, [handlePanelPointerMove, handlePdfPointerMove, handleMeasurementGripMove, handleMeasurementPointerMove, hvacMode, handleHvacPointerMove, structuralMode, handleStructuralPointerMove, updatePan, annotationMode, tool, handleAnnotationPointerMove, handlePointerMove]);

  const wrappedPointerUp = useCallback((e) => {
    if (handlePdfPointerUp()) return;
    // Handle measurement grip release
    if (draggingMeasurementGrip) {
      handleMeasurementGripEnd();
      return;
    }
    // Skip normal pointer up handling when measurement tool is active
    if (activeMeasureTool) return;
    if (hvacMode && handleHvacPointerUp(e)) return;
    if (hvacMode) {
      // In HVAC mode but HVAC didn't handle up - end panning
      endPan();
      return;
    }
    if (structuralMode && handleStructuralPointerUp(e)) return;
    if (structuralMode) {
      endPan();
      return;
    }
    if (annotationMode && tool.startsWith('annotation-')) {
      handleAnnotationPointerUp(e, tool);
    } else {
      handlePointerUp(e);
    }
  }, [handlePdfPointerUp, draggingMeasurementGrip, handleMeasurementGripEnd, activeMeasureTool, hvacMode, handleHvacPointerUp, structuralMode, handleStructuralPointerUp, endPan, annotationMode, tool, handleAnnotationPointerUp, handlePointerUp]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#080c10',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      {isMobile ? (
        <MobileToolbar
          tool={tool}
          onToolChange={setTool}
          onMenuToggle={() => setShowMobileMenu(!showMobileMenu)}
          onUndo={undo}
          onRedo={redo}
          hideFloatingButtons={hvacMode || structuralMode}
          onDelete={() => {
            if (selectedAnnotationIds.length > 0) {
              deleteSelectedAnnotations();
            } else if (selectedItems.length > 0) {
              deleteSelected(selectedItems);
            }
          }}
          onCopy={handleCopy}
          onPaste={handlePaste}
          hasClipboard={hasClipboard}
          pastePending={pendingPaste}
          canUndo={canUndo}
          canRedo={canRedo}
          hasSelection={selectedItems.length > 0 || selectedAnnotationIds.length > 0}
          activeTab={toolTab}
          onTabChange={(tab) => {
            setToolTab(tab);
            setAnnotationMode(tab === 'markup');
          }}
          onShowLayers={() => setActivePanel('layers')}
          onShowSettings={() => setActivePanel(activePanel === 'settings' ? null : 'settings')}
          onShowFurniture={() => setActivePanel(activePanel === 'furniture' ? null : 'furniture')}
          onShow3D={() => (hasSite3D ? setShowSite3D(true) : setShow3D(!show3D))}
          onShowElevations={() => {
            if (requirePro(PRO_FEATURES.ELEVATION_VIEW, 'Elevation Views')) {
              setViewMode('elevations');
            }
          }}
          onShowStamps={() => setActivePanel('stamps')}
          onShowAnnotationSettings={() => setActivePanel('annotation-settings')}
          onShowCalloutNotes={() => setActivePanel('callout-notes')}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetView={fitToContent}
          panelOpen={activePanel !== null}
          selectionMode={selectionMode}
          onSelectionModeChange={setSelectionMode}
        />
      ) : (
        <Toolbar
          tool={tool}
          onToolChange={setTool}
          toolTab={toolTab}
          onToolTabChange={(tab) => {
            setToolTab(tab);
            setAnnotationMode(tab === 'markup');
          }}
          selectionMode={selectionMode}
          onSelectionModeChange={setSelectionMode}
          onShowStamps={() => setActivePanel('stamps')}
          onShowAnnotationSettings={() => setActivePanel('annotation-settings')}
          onShowCalloutNotes={() => setActivePanel('callout-notes')}
          wallType={wallType}
          onWallTypeChange={setWallType}
          isMobile={isMobile}
        />
      )}

      {/* HVAC & Structural Mode Toggles — mobile: single flex row; desktop: separate absolute positions */}
      {isMobile ? (
        // Mobile: flex container so buttons sit side by side without overlapping
        viewMode === 'model' && !(hvacMode || structuralMode) && (
          <div style={{
            position: 'absolute',
            top: '52px',
            left: '12px',
            display: 'flex',
            gap: '6px',
            zIndex: 100,
          }}>
            <button
              onClick={() => {
                if (hvacMode || requirePro(PRO_FEATURES.HVAC_CALCULATIONS, 'HVAC Load Calculations')) {
                  toggleHvacMode();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: 'rgba(0,0,0,0.6)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                color: '#8090a0',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                backdropFilter: 'blur(4px)',
                position: 'relative',
              }}
              title="Enter HVAC Mode (Pro)"
            >
              <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="#8090a0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="6" width="18" height="4" rx="1" fill="#8090a0" fillOpacity="0.2" />
                <rect x="3" y="6" width="18" height="4" rx="1" />
                <path d="M7 10v6M12 10v8M17 10v6" />
                <circle cx="7" cy="16" r="2" fill="#8090a0" fillOpacity="0.2" />
                <circle cx="12" cy="18" r="2" fill="#8090a0" fillOpacity="0.2" />
                <circle cx="17" cy="16" r="2" fill="#8090a0" fillOpacity="0.2" />
              </svg>
              HVAC
              {!isPro && <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: 'white', fontSize: '8px', fontWeight: 'bold', padding: '2px 4px', borderRadius: '3px' }}>PRO</span>}
            </button>
            <button
              onClick={toggleStructuralMode}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: 'rgba(0,0,0,0.6)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                color: '#8090a0',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                backdropFilter: 'blur(4px)',
              }}
              title="Enter Structural Mode"
            >
              <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="#8090a0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="3" rx="0.5" fill="#8090a0" fillOpacity="0.2" />
                <rect x="3" y="4" width="18" height="3" rx="0.5" />
                <rect x="3" y="17" width="18" height="3" rx="0.5" fill="#8090a0" fillOpacity="0.2" />
                <rect x="3" y="17" width="18" height="3" rx="0.5" />
                <rect x="10" y="7" width="4" height="10" fill="#8090a0" fillOpacity="0.15" />
                <rect x="10" y="7" width="4" height="10" />
              </svg>
              Struct
            </button>
          </div>
        )
      ) : (
        // Desktop: separate absolute-positioned buttons on the right
        <>
          {viewMode === 'model' && (
            <div style={{
              position: 'absolute',
              top: '60px',
              right: '12px',
              zIndex: 100,
            }}>
              <button
                onClick={() => {
                  if (hvacMode || requirePro(PRO_FEATURES.HVAC_CALCULATIONS, 'HVAC Load Calculations')) {
                    toggleHvacMode();
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  background: hvacMode ? 'rgba(230, 81, 0, 0.3)' : 'rgba(0,0,0,0.6)',
                  border: `1px solid ${hvacMode ? '#e65100' : 'rgba(255,255,255,0.2)'}`,
                  borderRadius: '6px',
                  color: hvacMode ? '#ff9800' : '#8090a0',
                  fontSize: '12px',
                  fontWeight: hvacMode ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  backdropFilter: 'blur(4px)',
                  position: 'relative',
                }}
                title={hvacMode ? 'Exit HVAC Mode' : 'Enter HVAC Mode (Pro)'}
              >
                <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke={hvacMode ? '#ff9800' : '#8090a0'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="6" width="18" height="4" rx="1" fill={hvacMode ? '#ff9800' : '#8090a0'} fillOpacity="0.2" />
                  <rect x="3" y="6" width="18" height="4" rx="1" />
                  <path d="M7 10v6M12 10v8M17 10v6" />
                  <circle cx="7" cy="16" r="2" fill={hvacMode ? '#ff9800' : '#8090a0'} fillOpacity="0.2" />
                  <circle cx="12" cy="18" r="2" fill={hvacMode ? '#ff9800' : '#8090a0'} fillOpacity="0.2" />
                  <circle cx="17" cy="16" r="2" fill={hvacMode ? '#ff9800' : '#8090a0'} fillOpacity="0.2" />
                </svg>
                HVAC
                {!isPro && !hvacMode && <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: 'white', fontSize: '8px', fontWeight: 'bold', padding: '2px 4px', borderRadius: '3px' }}>PRO</span>}
              </button>
            </div>
          )}
          {viewMode === 'model' && (
            <div style={{
              position: 'absolute',
              top: '60px',
              right: hvacMode ? '12px' : '90px',
              zIndex: 100,
            }}>
              <button
                onClick={toggleStructuralMode}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  background: structuralMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(0,0,0,0.6)',
                  border: `1px solid ${structuralMode ? '#4caf50' : 'rgba(255,255,255,0.2)'}`,
                  borderRadius: '6px',
                  color: structuralMode ? '#66bb6a' : '#8090a0',
                  fontSize: '12px',
                  fontWeight: structuralMode ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  backdropFilter: 'blur(4px)',
                }}
                title={structuralMode ? 'Exit Structural Mode' : 'Enter Structural Mode'}
              >
                <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke={structuralMode ? '#66bb6a' : '#8090a0'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="3" rx="0.5" fill={structuralMode ? '#66bb6a' : '#8090a0'} fillOpacity="0.2" />
                  <rect x="3" y="4" width="18" height="3" rx="0.5" />
                  <rect x="3" y="17" width="18" height="3" rx="0.5" fill={structuralMode ? '#66bb6a' : '#8090a0'} fillOpacity="0.2" />
                  <rect x="3" y="17" width="18" height="3" rx="0.5" />
                  <rect x="10" y="7" width="4" height="10" fill={structuralMode ? '#66bb6a' : '#8090a0'} fillOpacity="0.15" />
                  <rect x="10" y="7" width="4" height="10" />
                </svg>
                Struct
              </button>
            </div>
          )}
        </>
      )}

      {/* HVAC Toolbar - show when HVAC mode is active */}
      {hvacMode && viewMode === 'model' && (
        <HVACToolbar
          activeTool={hvacTool}
          onToolChange={handleHvacToolChange}
          ductType={ductType}
          onDuctTypeChange={setDuctType}
          ductShape={ductShape}
          onDuctShapeChange={setDuctShape}
          ductSize={ductSize}
          onDuctSizeChange={setDuctSize}
          onShowVentPlacer={(type) => {
            setVentType(type);
            setShowVentPlacer(true);
          }}
          onShowEquipmentLibrary={() => setShowEquipmentLibrary(true)}
          onShowRoomData={() => {
            // If a room is selected, open its data panel
            const selectedRoom = selectedItems.find(s => s.type === 'room');
            if (selectedRoom) {
              handleOpenRoomData(selectedRoom.item);
            } else if (activeFloor?.rooms?.length > 0) {
              // If no room selected but rooms exist, show first room
              handleOpenRoomData(activeFloor.rooms[0]);
            }
          }}
          onShowLoadCalc={() => setShowLoadCalcPanel(!showLoadCalcPanel)}
          onShowDuctulator={() => setShowDuctulator(!showDuctulator)}
          onShowLayers={() => setShowHVACLayers(!showHVACLayers)}
          onShowSummary={() => setShowHVACSummary(!showHVACSummary)}
          onShowExport={() => setShowHVACExport(true)}
          onShowStamps={() => setShowHVACStamps(true)}
          onShowLabels={(type) => {
            setLabelPlacerType(type);
            setShowLabelPlacer(true);
          }}
          showLabelsOnDucts={showLabelsOnDucts}
          onToggleDuctLabels={() => setShowLabelsOnDucts(!showLabelsOnDucts)}
          showLayers={showHVACLayers}
          showSummary={showHVACSummary}
          showLoadCalc={showLoadCalcPanel}
          showDuctulator={showDuctulator}
          isMobile={isMobile}
          onClose={toggleHvacMode}
        />
      )}

      {/* Structural Toolbar - show when Structural mode is active */}
      {structuralMode && viewMode === 'model' && (
        <StructuralToolbar
          activeTool={structuralTool}
          onToolChange={setStructuralTool}
          buildingType={buildingType}
          onBuildingTypeChange={setBuildingType}
          onShowJoistCalc={() => setShowJoistCalc(!showJoistCalc)}
          showJoistCalc={showJoistCalc}
          onShowBeamCalc={() => setShowBeamCalc(!showBeamCalc)}
          showBeamCalc={showBeamCalc}
          isMobile={isMobile}
          onClose={toggleStructuralMode}
        />
      )}

      {/* Main content area */}
      <div style={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Touch paste: prompt for the spot. Anchored to the canvas area so it
            clears the toolbar on every layout. */}
        {pendingPaste && (
          <div
            style={{
              position: 'absolute',
              // On mobile the floating tool buttons overlay the top of the
              // canvas area, so the prompt sits below them.
              top: isMobile ? '108px' : '12px',
              left: '50%',
              transform: 'translateX(-50%)',
              // Without an explicit width, left:50% shrinks the box to half the
              // area and wraps the label into the button.
              width: 'max-content',
              maxWidth: 'calc(100% - 24px)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              whiteSpace: 'nowrap',
              background: 'rgba(8,12,16,0.95)',
              border: '1px solid rgba(0,200,255,0.5)',
              borderRadius: '8px',
              padding: '10px 14px',
              color: '#00c8ff',
              fontSize: '13px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
              zIndex: 60,
            }}
          >
            <span>Tap where to paste</span>
            <button
              onClick={() => setPendingPaste(false)}
              style={{
                background: 'transparent',
                border: '1px solid rgba(0,200,255,0.5)',
                color: '#00c8ff',
                borderRadius: '5px',
                padding: '4px 10px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Copy/paste feedback on mobile, which has no status bar */}
        {isMobile && clipboardMessage && !pendingPaste && (
          <div
            style={{
              position: 'absolute',
              // Clear of the mobile tab row, which overlays the canvas bottom
              bottom: '76px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'max-content',
              maxWidth: 'calc(100% - 24px)',
              background: 'rgba(8,12,16,0.95)',
              border: '1px solid rgba(0,200,255,0.4)',
              borderRadius: '8px',
              padding: '8px 14px',
              color: '#00c8ff',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
              zIndex: 60,
              pointerEvents: 'none',
            }}
          >
            {clipboardMessage}
          </div>
        )}

        {/* Floor Plan Canvas */}
        <FloorPlanCanvas
          ref={canvasRef}
          activeFloor={activeFloor}
          selectedItems={selectedItems}
          scale={zoom}
          offset={offset}
          tool={tool}
          wallType={wallType}
          wallDetailLevel={wallDetailLevel}
          thinLines={thinLines}
          isDrawing={isDrawing}
          drawStart={drawStart}
          drawEnd={drawEnd}
          polylinePoints={polylinePoints}
          hatchPoints={hatchPoints}
          roomPoints={roomPoints}
          windowSelection={windowSelection}
          selectedVertexIndex={selectedVertexIndex}
          moveBasePoint={moveBasePoint}
          movePreviewPoint={movePreviewPoint}
          rotateCenter={rotateCenter}
          rotateStartAngle={rotateStartAngle}
          rotatePreviewAngle={rotatePreviewAngle}
          snapGuidelines={snapGuidelines}
          activeSnap={activeSnap}
          polarAngle={polarAngle}
          gripCursorPosition={gripCursorPosition}
          units={units}
          gridSize={gridSize}
          showDimensions={showDimensions}
          showTempDimensions={showTempDimensions}
          showGrips={showGrips}
          fontStyle={fontStyle}
          isMobile={isMobile}
          layers={layers}
          pdfLayer={pdfLayer}
          gradingOverlay={gradingResult?.overlay || null}
          panelPreview={panelPreview}
          annotations={annotations}
          selectedAnnotationIds={selectedAnnotationIds}
          drawingAnnotation={drawingAnnotation}
          annotationsAboveFloorPlan={annotationSettings.aboveFloorPlan}
          hvacMode={hvacMode}
          hvacTool={hvacTool}
          ductType={ductType}
          ductShape={ductShape}
          ductSize={ductSize}
          ductPreviewPoints={ductPreviewPoints}
          selectedDuctId={selectedDuctId}
          ventType={ventType}
          ventSize={ventSize}
          ventRotation={ventRotation}
          selectedVentId={selectedVentId}
          equipmentType={equipmentType}
          equipmentRotation={equipmentRotation}
          selectedEquipmentId={selectedEquipmentId}
          fittingType={fittingType}
          fittingRotation={fittingRotation}
          selectedFittingId={selectedFittingId}
          hvacPreviewPosition={hvacPreviewPosition}
          hvacLayers={hvacLayerState}
          selectedStampType={selectedStampType}
          selectedHvacStamp={selectedHvacStamp}
          selectedHvacLabel={selectedHvacLabel}
          pendingLabel={pendingLabel}
          showLabelsOnDucts={showLabelsOnDucts}
          showLoadLabels={showLoadLabels}
          designConditions={designConditions}
          structuralMode={structuralMode}
          structuralTool={structuralTool}
          selectedJoistId={selectedJoistId}
          isDrawingJoist={isDrawingJoist}
          joistDrawStart={joistDrawStart}
          joistDrawEnd={joistDrawEnd}
          joistDirection={joistDefaults.direction}
          joistSpacing={joistDefaults.spacing}
          onPointerDown={wrappedPointerDown}
          onPointerMove={wrappedPointerMove}
          onPointerUp={wrappedPointerUp}
          onDoubleClick={handleDoubleClick}
          onWheel={onWheel}
        />

        {/* PDF Page Sidebar Toggle - only show for multi-page PDFs */}
        {pdfLayer?.dataUrl && pdfLayer?.pageCount > 1 && (
          <PDFPageSidebarToggle
            onClick={() => setShowPdfPageSidebar(!showPdfPageSidebar)}
            isOpen={showPdfPageSidebar}
            pageCount={pdfLayer.pageCount}
            currentPage={pdfLayer.currentPage}
            isMobile={isMobile}
          />
        )}

        {/* PDF Page Sidebar */}
        {showPdfPageSidebar && pdfLayer?.dataUrl && pdfLayer?.pageCount > 1 && (
          <PDFPageSidebar
            pdfLayer={pdfLayer}
            onChangePage={changePdfPage}
            onRelinkPdf={relinkPdfDocument}
            onClose={() => setShowPdfPageSidebar(false)}
            isMobile={isMobile}
            onDeletePages={deletePdfPages}
            onRotatePage={rotatePdfPage}
            onAddBlankPage={addBlankPdfPage}
            onExtractPages={(pageNumbers) => {
              const extractedData = extractPdfPages(pageNumbers);
              if (extractedData) {
                // Create a new project file with the extracted pages data
                const blob = new Blob([JSON.stringify(extractedData, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `extracted-pages-${pageNumbers.join('-')}.floorplan`;
                a.click();
                URL.revokeObjectURL(url);
              }
            }}
          />
        )}

        {/* PDF Measurement Renderer - SVG overlay for measurements */}
        {pdfLayer?.dataUrl && pdfLayer?.scale && (
          <PDFMeasurementRenderer
            measurements={pdfMeasurements}
            selectedIds={selectedMeasurementIds}
            drawingMeasurement={drawingMeasurement}
            scale={zoom}
            offset={offset}
            units={units}
            onMeasurementClick={handleMeasurementClick}
            onGripStart={handleMeasurementGripStart}
            onGripEnd={handleMeasurementGripEnd}
            onGripMove={handleMeasurementGripMove}
            onCanvasClick={handleMeasurementPointerDown}
            onCanvasMove={handleMeasurementPointerMove}
            onWheel={onWheel}
            isDraggingGrip={!!draggingMeasurementGrip}
            isDrawingMeasurement={!!activeMeasureTool}
            isAnnotationCloudToolActive={annotationMode && tool === 'annotation-cloud'}
            isDrawingAnnotationCloud={!!(drawingAnnotation && drawingAnnotation.type === 'cloud')}
            onAnnotationClick={(e) => handleAnnotationPointerDown(e, tool)}
            onAnnotationMove={(e) => handleAnnotationPointerMove(e, tool)}
            onAnnotationDoubleClick={(e) => handleAnnotationDoubleClick(e, tool)}
          />
        )}

        {/* PDF Measurement Toolbar - show when PDF is calibrated */}
        {pdfLayer?.dataUrl && pdfLayer?.scale && (
          <div style={{
            position: 'absolute',
            top: isMobile ? 10 : 16,
            left: isMobile ? 60 : 160,
            zIndex: 100,
          }}>
            <PDFMeasurementToolbar
              activeTool={activeMeasureTool}
              onToolChange={(tool) => {
                setActiveMeasureTool(tool);
                setDrawingMeasurement(null);
                if (tool) {
                  setSelectedMeasurementIds([]);
                }
              }}
              onClearAll={() => {
                clearPdfMeasurements();
                setSelectedMeasurementIds([]);
              }}
              measurementCount={pdfMeasurements.length}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* PDF Measurement Totals Panel */}
        {showMeasurementTotals && pdfMeasurements.length > 0 && (
          <PDFMeasurementTotals
            measurements={pdfMeasurements}
            selectedIds={selectedMeasurementIds}
            units={units}
            onClose={() => setShowMeasurementTotals(false)}
            isMobile={isMobile}
          />
        )}

        {/* Editable temporary dimensions for selected doors */}
        {selectedItem?.type === 'door' && (() => {
          const wall = activeFloor?.walls?.find(w => w.id === selectedItem.item.wallId);
          if (!wall) return null;
          return (
            <TempDimensionEditor
              element={selectedItem.item}
              elementType="door"
              wall={wall}
              scale={zoom}
              offset={offset}
              units={units}
              wallDetailLevel={wallDetailLevel}
              onUpdate={(updates) => {
                const door = selectedItem.item;
                const oldPosition = door.position;
                const newPosition = updates.position !== undefined ? updates.position : oldPosition;

                // Calculate old and new absolute positions
                const wallDx = wall.end.x - wall.start.x;
                const wallDy = wall.end.y - wall.start.y;
                const wallLength = Math.sqrt(wallDx * wallDx + wallDy * wallDy);

                const oldAbsPos = oldPosition * wallLength;
                const newAbsPos = newPosition * wallLength;

                const oldCenter = {
                  x: wall.start.x + (oldAbsPos / wallLength) * wallDx,
                  y: wall.start.y + (oldAbsPos / wallLength) * wallDy
                };
                const newCenter = {
                  x: wall.start.x + (newAbsPos / wallLength) * wallDx,
                  y: wall.start.y + (newAbsPos / wallLength) * wallDy
                };

                // Threshold for considering a dimension endpoint as "snapped" to the door
                const snapThreshold = 15;

                updateActiveFloor(f => {
                  // Update the door
                  const updatedDoors = f.doors.map(d =>
                    d.id === selectedItem.item.id ? { ...d, ...updates } : d
                  );

                  // Update any dimensions that have endpoints near the old door center
                  const updatedDimensions = (f.dimensions || []).map(dim => {
                    let updated = { ...dim };

                    // Check start point
                    const startDist = Math.sqrt(
                      Math.pow(dim.start.x - oldCenter.x, 2) +
                      Math.pow(dim.start.y - oldCenter.y, 2)
                    );
                    if (startDist < snapThreshold) {
                      updated.start = { ...newCenter };
                    }

                    // Check end point
                    const endDist = Math.sqrt(
                      Math.pow(dim.end.x - oldCenter.x, 2) +
                      Math.pow(dim.end.y - oldCenter.y, 2)
                    );
                    if (endDist < snapThreshold) {
                      updated.end = { ...newCenter };
                    }

                    return updated;
                  });

                  return {
                    ...f,
                    doors: updatedDoors,
                    dimensions: updatedDimensions
                  };
                });

                setSelectedItems(prev => prev.map(s => {
                  if (s.type === 'door' && s.item?.id === selectedItem.item.id) {
                    return { ...s, item: { ...s.item, ...updates } };
                  }
                  return s;
                }));
              }}
            />
          );
        })()}

        {/* Editable temporary dimensions for selected windows */}
        {selectedItem?.type === 'window' && (() => {
          const wall = activeFloor?.walls?.find(w => w.id === selectedItem.item.wallId);
          if (!wall) return null;
          return (
            <TempDimensionEditor
              element={selectedItem.item}
              elementType="window"
              wall={wall}
              scale={zoom}
              offset={offset}
              units={units}
              wallDetailLevel={wallDetailLevel}
              onUpdate={(updates) => {
                const window = selectedItem.item;
                const oldPosition = window.position;
                const newPosition = updates.position !== undefined ? updates.position : oldPosition;

                // Calculate old and new absolute positions
                const wallDx = wall.end.x - wall.start.x;
                const wallDy = wall.end.y - wall.start.y;
                const wallLength = Math.sqrt(wallDx * wallDx + wallDy * wallDy);

                const oldAbsPos = oldPosition * wallLength;
                const newAbsPos = newPosition * wallLength;

                const oldCenter = {
                  x: wall.start.x + (oldAbsPos / wallLength) * wallDx,
                  y: wall.start.y + (oldAbsPos / wallLength) * wallDy
                };
                const newCenter = {
                  x: wall.start.x + (newAbsPos / wallLength) * wallDx,
                  y: wall.start.y + (newAbsPos / wallLength) * wallDy
                };

                // Threshold for considering a dimension endpoint as "snapped" to the window
                const snapThreshold = 15;

                updateActiveFloor(f => {
                  // Update the window
                  const updatedWindows = f.windows.map(w =>
                    w.id === selectedItem.item.id ? { ...w, ...updates } : w
                  );

                  // Update any dimensions that have endpoints near the old window center
                  const updatedDimensions = (f.dimensions || []).map(dim => {
                    let updated = { ...dim };

                    // Check start point
                    const startDist = Math.sqrt(
                      Math.pow(dim.start.x - oldCenter.x, 2) +
                      Math.pow(dim.start.y - oldCenter.y, 2)
                    );
                    if (startDist < snapThreshold) {
                      updated.start = { ...newCenter };
                    }

                    // Check end point
                    const endDist = Math.sqrt(
                      Math.pow(dim.end.x - oldCenter.x, 2) +
                      Math.pow(dim.end.y - oldCenter.y, 2)
                    );
                    if (endDist < snapThreshold) {
                      updated.end = { ...newCenter };
                    }

                    return updated;
                  });

                  return {
                    ...f,
                    windows: updatedWindows,
                    dimensions: updatedDimensions
                  };
                });

                setSelectedItems(prev => prev.map(s => {
                  if (s.type === 'window' && s.item?.id === selectedItem.item.id) {
                    return { ...s, item: { ...s.item, ...updates } };
                  }
                  return s;
                }));
              }}
            />
          );
        })()}

        {/* Editable temporary dimension for selected walls */}
        {selectedItem?.type === 'wall' && (
          <WallTempDimensionEditor
            wall={selectedItem.item}
            scale={zoom}
            offset={offset}
            units={units}
            wallDetailLevel={wallDetailLevel}
            onUpdate={(updates) => {
              updateWall(selectedItem.item.id, updates);
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'wall' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onUpdateWithElements={(updates, oldLength, newLength) => {
              const wallId = selectedItem.item.id;

              // Update wall and recalculate door/window positions to keep them at absolute positions
              updateActiveFloor(f => {
                // Update doors on this wall - convert position to absolute, then back to new relative
                const updatedDoors = f.doors.map(door => {
                  if (door.wallId !== wallId) return door;
                  // Current absolute position along wall
                  const absolutePos = door.position * oldLength;
                  // New relative position for new wall length
                  const newPosition = absolutePos / newLength;
                  // Clamp to valid range (0-1)
                  return { ...door, position: Math.max(0.05, Math.min(0.95, newPosition)) };
                });

                // Update windows on this wall
                const updatedWindows = f.windows.map(window => {
                  if (window.wallId !== wallId) return window;
                  const absolutePos = window.position * oldLength;
                  const newPosition = absolutePos / newLength;
                  return { ...window, position: Math.max(0.05, Math.min(0.95, newPosition)) };
                });

                // Update the wall
                const updatedWalls = f.walls.map(w =>
                  w.id === wallId ? { ...w, ...updates } : w
                );

                return {
                  ...f,
                  walls: updatedWalls,
                  doors: updatedDoors,
                  windows: updatedWindows,
                };
              });

              // Update selected item
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'wall' && s.item?.id === wallId) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
          />
        )}

        {/* Quick action buttons (floating) - desktop only */}
        {!isMobile && (
          <div
            style={{
              position: 'absolute',
              bottom: 60,
              right: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              background: 'rgba(12,18,24,0.95)',
              padding: '8px',
              borderRadius: '10px',
              border: '1px solid rgba(0,200,255,0.2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}
          >
            <Button variant="default" onClick={zoomIn} title="Zoom In">
              <TOOLBAR_ICONS.zoomIn color="#8090a0" />
            </Button>
            <Button variant="default" onClick={zoomOut} title="Zoom Out">
              <TOOLBAR_ICONS.zoomOut color="#8090a0" />
            </Button>
            <Button variant="default" onClick={fitToContent} title="Fit to Content">
              <TOOLBAR_ICONS.resetView color="#8090a0" />
            </Button>
            <Button variant="default" onClick={() => setActivePanel('layers')} title="Layers">
              <TOOLBAR_ICONS.layers color="#8090a0" />
            </Button>
            <Button variant="default" onClick={() => setActivePanel('sheets')} title="Sheets & Views">
              <TOOLBAR_ICONS.sheets color="#8090a0" />
            </Button>
            <Button variant="default" onClick={() => setActivePanel('furniture')} title="Furniture">
              <TOOLBAR_ICONS.chair color="#8090a0" />
            </Button>
            <Button variant="default" onClick={() => setActivePanel('fire-safety')} title="Fire & Life Safety">
              <TOOLBAR_ICONS.fireSafety color="#8090a0" />
            </Button>
            <Button
              variant={pdfLayer?.dataUrl ? 'primary' : 'default'}
              onClick={() => {
                if (pdfLayer?.dataUrl || requirePro(PRO_FEATURES.PDF_IMPORT, 'PDF Import')) {
                  setActivePanel(pdfLayer?.dataUrl ? 'pdf-controls' : 'pdf-import');
                }
              }}
              title={pdfLayer?.dataUrl ? 'PDF Controls' : 'Import PDF (Pro)'}
            >
              <TOOLBAR_ICONS.pdf color={pdfLayer?.dataUrl ? '#00c8ff' : '#8090a0'} />
              {!isPro && !pdfLayer?.dataUrl && <ProBadge className="absolute -top-1 -right-1" />}
            </Button>
            <Button
              variant={terrain ? 'primary' : 'default'}
              onClick={() => setActivePanel(activePanel === 'grading' ? null : 'grading')}
              title="Site Grading"
            >
              <TOOLBAR_ICONS.terrain color={terrain ? '#00c8ff' : '#8090a0'} />
            </Button>
            <Button
              variant={activePanel === 'arena-panels' ? 'primary' : 'default'}
              onClick={() => setActivePanel(activePanel === 'arena-panels' ? null : 'arena-panels')}
              title="Arena Panels"
            >
              <TOOLBAR_ICONS.panel color={activeFloor?.panels?.length ? '#00c8ff' : '#8090a0'} />
            </Button>

            <Button variant="default" onClick={() => {
              if (requirePro(PRO_FEATURES.ELEVATION_VIEW, 'Elevation Views')) {
                setViewMode('elevations');
              }
            }} title="Elevation Views (Pro)">
              <TOOLBAR_ICONS.elevations color="#8090a0" />
              {!isPro && <ProBadge className="absolute -top-1 -right-1" />}
            </Button>
            <Button
              variant="default"
              onClick={() => (hasSite3D ? setShowSite3D(true) : setShow3D(!show3D))}
              title={hasSite3D ? '3D Site View' : '3D View'}
            >
              <TOOLBAR_ICONS.view3d color={hasSite3D ? '#00c8ff' : '#8090a0'} />
            </Button>
            <Button variant="default" onClick={() => {
              if (requirePro(PRO_FEATURES.MATERIAL_CALCULATOR, 'Material Calculator')) {
                setShowMaterialCalculator(true);
              }
            }} title="Material Calculator (Pro)">
              <TOOLBAR_ICONS.calculator color="#8090a0" />
              {!isPro && <ProBadge className="absolute -top-1 -right-1" />}
            </Button>
            <Button variant="default" onClick={() => setShowShareModal(true)} title="Share">
              <TOOLBAR_ICONS.share color="#8090a0" />
            </Button>
          </div>
        )}

        {/* Undo/Redo buttons - desktop only */}
        {!isMobile && (
          <div
            style={{
              position: 'absolute',
              bottom: 60,
              left: 16,
              display: 'flex',
              gap: '6px',
              background: 'rgba(12,18,24,0.95)',
              padding: '8px',
              borderRadius: '10px',
              border: '1px solid rgba(0,200,255,0.2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}
          >
            <Button
              variant="default"
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            >
              <TOOLBAR_ICONS.undo color={canUndo ? '#8090a0' : '#4a5a6a'} />
            </Button>
            <Button
              variant="default"
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
            >
              <TOOLBAR_ICONS.redo color={canRedo ? '#8090a0' : '#4a5a6a'} />
            </Button>
          </div>
        )}

        {/* Library Panels */}
        {activePanel === 'furniture' && (
          <FurnitureLibrary
            onSelect={(item) => {
              const rect = canvasRef.current?.getBoundingClientRect?.();
              const center = rect
                ? screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2)
                : { x: 200, y: 200 };
              addFurniture({
                ...item,
                x: center.x,
                y: center.y,
                rotation: 0,
              });
              setActivePanel(null);
            }}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}
        {activePanel === 'stairs' && (
          <StairsLibrary
            onSelect={(item) => {
              // Add stairs logic
              setActivePanel(null);
            }}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}
        {activePanel === 'electrical' && (
          <ElectricalLibrary
            onSelect={(item) => {
              // Add electrical logic
              setActivePanel(null);
            }}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}
        {activePanel === 'plumbing' && (
          <PlumbingLibrary
            onSelect={(item) => {
              // Add plumbing logic
              setActivePanel(null);
            }}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}
        {activePanel === 'fire-safety' && (
          <FireSafetyLibrary
            onSelect={(item) => {
              addFireSafety({
                ...item,
                x: 200,
                y: 200,
                rotation: item.rotation ?? 0,
              });
              setActivePanel(null);
            }}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}
        {activePanel === 'layers' && (
          <LayersPanel
            layers={layers}
            onToggleVisibility={toggleVisibility}
            onToggleLock={toggleLock}
            onSetColor={setColor}
            onShowAll={showAll}
            onHideAll={hideAll}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
            hvacMode={hvacMode}
            hvacLayers={hvacLayerState}
            onUpdateHvacLayer={(layerId, state) => {
              setHvacLayerState(prev => ({
                ...prev,
                [layerId]: state,
              }));
            }}
          />
        )}
        {activePanel === 'settings' && (
          <SettingsPanel
            units={units}
            setUnits={setUnits}
            gridSize={gridSize}
            setGridSize={setGridSize}
            angleSnap={angleSnap}
            setAngleSnap={setAngleSnap}
            showDimensions={showDimensions}
            setShowDimensions={setShowDimensions}
            showTempDimensions={showTempDimensions}
            setShowTempDimensions={setShowTempDimensions}
            showGrips={showGrips}
            setShowGrips={setShowGrips}
            thinLines={thinLines}
            setThinLines={setThinLines}
            wallDetailLevel={wallDetailLevel}
            setWallDetailLevel={setWallDetailLevel}
            fontStyle={fontStyle}
            setFontStyle={setFontStyle}
            snaps={snaps}
            setSnaps={setSnaps}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}
        {activePanel === 'sheets' && (
          <SheetsPanel
            sheets={sheets}
            activeSheetId={activeSheetId}
            onSelectSheet={handleSelectSheet}
            onAddSheet={handleAddSheet}
            onUpdateSheet={handleUpdateSheet}
            onDeleteSheet={handleDeleteSheet}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}

        {/* PDF Import Panel */}
        {activePanel === 'pdf-import' && (
          <PDFImporter
            onImport={(pdfData) => {
              importPdfLayer(pdfData);
              setActivePanel(null);
              if (pdfData.geo) {
                // World file already set the scale - just bring the image into view
                setTimeout(() => zoomToFit({ minX: 0, minY: 0, maxX: pdfData.width, maxY: pdfData.height }, 50), 100);
              } else {
                // Prompt to calibrate scale after import
                setShowScaleCalibration(true);
              }
            }}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}

        {/* PDF Controls Panel */}
        {activePanel === 'pdf-controls' && pdfLayer?.dataUrl && (
          <PDFControls
            pdfLayer={pdfLayer}
            onUpdate={updatePdfLayer}
            onRemove={() => {
              removePdfLayer();
              setActivePanel(null);
              setIsMovingPdf(false);
            }}
            onRecalibrate={() => setShowScaleCalibration(true)}
            onStartMove={() => setIsMovingPdf(!isMovingPdf)}
            onChangePage={changePdfPage}
            isMoving={isMovingPdf}
            onClose={() => {
              setActivePanel(null);
              setIsMovingPdf(false);
            }}
            isMobile={isMobile}
          />
        )}

        {/* Site Grading Panel */}
        {activePanel === 'grading' && (
          <GradingPanel
            terrain={terrain}
            hasGeoLayer={!!(pdfLayer?.dataUrl && pdfLayer?.geo)}
            rooms={activeFloor?.rooms || []}
            grading={grading}
            onChangeGrading={setGrading}
            onLoadTerrain={handleLoadTerrain}
            onRemoveTerrain={() => setTerrain(null)}
            onAddArena={handleAddArena}
            arenaShape={arenaShape}
            onResizeArena={handleResizeArena}
            result={gradingResult?.result || null}
            siteModel={siteModel}
            missingSiteModel={missingSiteModel}
            onLoadSiteModel={handleLoadSiteModel}
            onRemoveSiteModel={() => setSiteModel(null)}
            onShow3D={() => setShowSite3D(true)}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}

        {/* Arena Panels */}
        {activePanel === 'arena-panels' && (
          <ArenaPanelsPanel
            choice={panelChoice}
            onChangeChoice={setPanelChoice}
            placing={tool === 'panel'}
            onTogglePlacing={() => setTool(tool === 'panel' ? 'select' : 'panel')}
            rooms={activeFloor?.rooms || []}
            fence={{ ...panelFence, roomId: fenceRoomId }}
            onChangeFence={setPanelFence}
            onFenceOutline={() => handleFenceOutline()}
            fenceReport={fenceReport}
            onFitOutline={handleFitOutline}
            canFitOutline={!!fenceRoom}
            panels={activeFloor?.panels || []}
            onClearFence={() => {
              updateActiveFloor(f => ({ ...f, panels: (f.panels || []).filter(p => p.fenceId !== fenceRoomId) }));
              setSelectedItems(prev => prev.filter(s => s.type !== 'panel'));
              setFenceReport(null);
            }}
            onClearAll={() => {
              updateActiveFloor(f => ({ ...f, panels: [] }));
              setSelectedItems(prev => prev.filter(s => s.type !== 'panel'));
              setFenceReport(null);
            }}
            layoutReference={(() => {
              const preset = ARENA_PRESETS.find(p => p.id === (fenceRoom && describeArena(fenceRoom))?.presetId);
              return preset?.reference ? { label: preset.label, ...preset.reference } : null;
            })()}
            canSetupEquipment={!!equipmentRoom}
            onSetupRopingEnd={(flipped) => handleArenaSetup('roping', flipped)}
            onSetupStrippingEnd={(flipped) => handleArenaSetup('stripping', flipped)}
            onShow3D={() => setShowSite3D(true)}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}

        {/* 3D Site View */}
        {showSite3D && (
          <SiteView3DBoundary onClose={() => setShowSite3D(false)}>
          <Suspense fallback={
            <div style={{ position: 'fixed', inset: 0, zIndex: 20000, background: 'rgba(8,12,16,0.9)', color: '#00c8ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Loading 3D view…
            </div>
          }>
            <SiteView3D
              terrain={pdfLayer?.geo ? terrain : null}
              layer={pdfLayer}
              panels={activeFloor?.panels || []}
              equipment={(activeFloor?.furniture || []).filter(f => f.category === 'arena')}
              siteModel={siteModel}
              arenaPoints={arenaRoom?.points || null}
              grading={gradingResult?.result || null}
              onClose={() => setShowSite3D(false)}
            />
          </Suspense>
          </SiteView3DBoundary>
        )}

        {/* Scale Calibration Panel */}
        {showScaleCalibration && pdfLayer?.dataUrl && (
          <ScaleCalibration
            pdfLayer={pdfLayer}
            canvasRef={canvasRef}
            scale={zoom}
            offset={offset}
            onCalibrate={handleCalibrateScale}
            onClose={() => setShowScaleCalibration(false)}
            isMobile={isMobile}
          />
        )}

        {/* Stamp Selector Panel */}
        {activePanel === 'stamps' && (
          <StampSelector
            selectedStamp={annotationSettings.stampType}
            customStampText={annotationSettings.customStampText}
            onSelect={(stampType) => {
              updateAnnotationSettings({ stampType });
              setTool('annotation-stamp');
              setActivePanel(null);
            }}
            onCustomTextChange={(text) => {
              updateAnnotationSettings({ customStampText: text });
            }}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}

        {/* Annotation Settings Panel */}
        {activePanel === 'annotation-settings' && (
          <AnnotationSettingsPanel
            settings={annotationSettings}
            onSettingsChange={updateAnnotationSettings}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}

        {/* Callout Notes Panel */}
        {activePanel === 'callout-notes' && (
          <CalloutNotesPanel
            annotations={annotations}
            onUpdateAnnotation={updateAnnotation}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
          />
        )}

        {/* Export Modal */}
        {activePanel === 'export' && (
          <ExportModal
            onExport={handleExport}
            onClose={() => setActivePanel(null)}
            isMobile={isMobile}
            projectName={projectName}
            companyName=""
          />
        )}

        {/* Save Dialog */}
        {showSaveDialog && (
          <SaveDialog
            defaultName={projectName}
            onSave={handleSaveWithName}
            onClose={() => setShowSaveDialog(false)}
            isMobile={isMobile}
          />
        )}

        {/* Recent Projects Panel */}
        {showRecentProjects && (
          <RecentProjects
            onLoad={handleLoadFromRecent}
            onClose={() => setShowRecentProjects(false)}
            isMobile={isMobile}
          />
        )}

        {/* Material Calculator */}
        {showMaterialCalculator && (() => {
          // Scale: GRID_SIZE (20) pixels = 6 inches = 0.5 feet, so 40 pixels = 1 foot
          const PIXELS_PER_FOOT = 40;
          const floorArea = calculateTotalFloorArea(activeFloor?.walls || [], PIXELS_PER_FOOT);
          return (
            <MaterialCalculator
              sqft={floorArea.sqft}
              perimeter={floorArea.perimeter}
              walls={activeFloor?.walls || []}
              source={activeFloor?.walls?.length > 0 ? 'all' : 'manual'}
              onClose={() => setShowMaterialCalculator(false)}
              onCreateEstimate={(materials) => {
                setEstimateMaterials(materials);
                setShowMaterialCalculator(false);
                setShowEstimateEditor(true);
              }}
              isMobile={isMobile}
            />
          );
        })()}

        {/* Share Modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          projectData={{
            floors,
            pdfLayer,
            annotations,
            annotationSettings,
          }}
          projectName={projectName}
        />

        {/* PDF Export Modal */}
        {showPDFExportModal && (
          <PDFExportModal
            onClose={() => setShowPDFExportModal(false)}
            pdfLayer={pdfLayer}
            annotations={annotations}
            pdfMeasurements={pdfMeasurements}
            units={units}
            isMobile={isMobile}
          />
        )}

        {/* Company Profile Modal */}
        <CompanyProfileModal
          isOpen={showCompanyProfile}
          onClose={() => setShowCompanyProfile(false)}
        />

        {/* Template Selector Modal */}
        {showTemplateSelector && (
          <TemplateSelector
            onSelect={handleSelectTemplate}
            onClose={() => setShowTemplateSelector(false)}
          />
        )}

        {/* Client Manager Modal */}
        <ClientManager
          isOpen={showClientManager}
          onClose={() => setShowClientManager(false)}
        />

        {/* Estimate List */}
        <EstimateList
          isOpen={showEstimateList}
          onClose={() => setShowEstimateList(false)}
          estimates={estimates}
          onNew={() => {
            setEditingEstimate(null);
            setEstimateMaterials([]);
            setShowEstimateList(false);
            setShowEstimateEditor(true);
          }}
          onEdit={(estimate) => {
            setEditingEstimate(estimate);
            setEstimateMaterials([]);
            setShowEstimateList(false);
            setShowEstimateEditor(true);
          }}
          onDuplicate={(duplicatedEstimate) => {
            setEditingEstimate(null);
            // Open editor with the duplicated estimate data
            setEstimateMaterials([]);
            setShowEstimateList(false);
            // Pass as existing estimate but without ID so it creates new
            setEditingEstimate({ ...duplicatedEstimate, id: undefined });
            setShowEstimateEditor(true);
          }}
          onDelete={(estimateId) => {
            setEstimates(prev => prev.filter(e => e.id !== estimateId));
          }}
          onStatusChange={(estimateId, newStatus) => {
            setEstimates(prev => prev.map(e =>
              e.id === estimateId ? { ...e, status: newStatus } : e
            ));
          }}
          onConvertToInvoice={(estimate) => {
            setSourceEstimateForInvoice(estimate);
            setEditingInvoice(null);
            setShowEstimateList(false);
            setShowInvoiceEditor(true);
          }}
          isMobile={isMobile}
        />

        {/* Estimate Editor */}
        <EstimateEditor
          isOpen={showEstimateEditor}
          onClose={() => {
            setShowEstimateEditor(false);
            setEstimateMaterials([]);
            setEditingEstimate(null);
          }}
          onSave={(estimate) => {
            setEstimates(prev => {
              const existing = prev.findIndex(e => e.id === estimate.id);
              if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = estimate;
                return updated;
              }
              return [...prev, estimate];
            });
            setEditingEstimate(null);
          }}
          initialMaterials={estimateMaterials}
          existingEstimate={editingEstimate}
          projectName={projectName}
          isMobile={isMobile}
        />

        {/* Invoice List */}
        <InvoiceList
          isOpen={showInvoiceList}
          onClose={() => setShowInvoiceList(false)}
          invoices={invoices}
          onNew={() => {
            setEditingInvoice(null);
            setSourceEstimateForInvoice(null);
            setShowInvoiceList(false);
            setShowInvoiceEditor(true);
          }}
          onEdit={(invoice) => {
            setEditingInvoice(invoice);
            setSourceEstimateForInvoice(null);
            setShowInvoiceList(false);
            setShowInvoiceEditor(true);
          }}
          onDuplicate={(duplicatedInvoice) => {
            setEditingInvoice(null);
            setSourceEstimateForInvoice(null);
            setShowInvoiceList(false);
            // Pass as existing invoice but without ID so it creates new
            setEditingInvoice({ ...duplicatedInvoice, id: undefined, invoiceNumber: undefined });
            setShowInvoiceEditor(true);
          }}
          onDelete={(invoiceId) => {
            setInvoices(prev => prev.filter(i => i.id !== invoiceId));
          }}
          onStatusChange={(invoiceId, newStatus) => {
            setInvoices(prev => prev.map(i =>
              i.id === invoiceId ? { ...i, status: newStatus } : i
            ));
          }}
          isMobile={isMobile}
        />

        {/* Invoice Editor */}
        <InvoiceEditor
          isOpen={showInvoiceEditor}
          onClose={() => {
            setShowInvoiceEditor(false);
            setEditingInvoice(null);
            setSourceEstimateForInvoice(null);
          }}
          onSave={(invoice) => {
            setInvoices(prev => {
              const existing = prev.findIndex(i => i.id === invoice.id);
              if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = invoice;
                return updated;
              }
              return [...prev, invoice];
            });
            setEditingInvoice(null);
            setSourceEstimateForInvoice(null);
          }}
          existingInvoice={editingInvoice}
          sourceEstimate={sourceEstimateForInvoice}
          projectName={projectName}
          isMobile={isMobile}
        />

        {/* Property editors for selected items */}
        {/* On mobile, defer showing editors for just-drawn items to avoid interrupting workflow */}
        {selectedItem?.type === 'wall' && (!isMobile || selectionSource === 'click') && (
          <WallEditor
            wall={selectedItem.item}
            onUpdate={(updates) => {
              updateWall(selectedItem.item.id, updates);
              // Also update selectedItems to keep the editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'wall' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'door' && (!isMobile || selectionSource === 'click') && (
          <DoorEditor
            door={selectedItem.item}
            onUpdate={(updates) => {
              updateActiveFloor(f => ({
                ...f,
                doors: f.doors.map(d =>
                  d.id === selectedItem.item.id ? { ...d, ...updates } : d
                )
              }));
              // Also update selectedItems to keep editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'door' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            formatMeasurement={formatMeasurementFn}
            units={units}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'window' && (!isMobile || selectionSource === 'click') && (
          <WindowEditor
            window={selectedItem.item}
            onUpdate={(updates) => {
              updateActiveFloor(f => ({
                ...f,
                windows: f.windows.map(w =>
                  w.id === selectedItem.item.id ? { ...w, ...updates } : w
                )
              }));
              // Also update selectedItems to keep editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'window' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            formatMeasurement={formatMeasurementFn}
            units={units}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'furniture' && (!isMobile || selectionSource === 'click') && (
          <FurnitureEditor
            furniture={selectedItem.item}
            onUpdate={(updates) => {
              updateActiveFloor(f => ({
                ...f,
                furniture: f.furniture.map(furn =>
                  furn.id === selectedItem.item.id ? { ...furn, ...updates } : furn
                )
              }));
              // Also update selectedItems to keep the editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'furniture' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'fireSafety' && (!isMobile || selectionSource === 'click') && (
          <FireSafetyEditor
            item={selectedItem.item}
            onUpdate={(updates) => {
              updateFireSafety(selectedItem.item.id, updates);
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'fireSafety' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'roof' && (!isMobile || selectionSource === 'click') && (
          <RoofEditor
            roof={selectedItem.item}
            onUpdate={(updates) => {
              updateRoof(selectedItem.item.id, updates);
              // Also update selectedItems to keep the editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'roof' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'post' && (!isMobile || selectionSource === 'click') && (
          <PostEditor
            post={selectedItem.item}
            onUpdate={(updates) => {
              updatePost(selectedItem.item.id, updates);
              // Reuse these settings for the next post placed
              setPostSettings(prev => ({ ...prev, ...updates, x: undefined, y: undefined }));
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'post' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDuplicate={() => {
              const { id, x, y, ...rest } = selectedItem.item;
              addPost({ ...rest, x: x + 40, y });
            }}
            onDelete={() => deleteSelected(selectedItems)}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'text' && (!isMobile || selectionSource === 'click') && (
          <TextEditor
            text={selectedItem.item}
            onUpdate={(updates) => {
              updateActiveFloor(f => ({
                ...f,
                texts: (f.texts || []).map(t =>
                  t.id === selectedItem.item.id ? { ...t, ...updates } : t
                )
              }));
              // Also update selectedItems to keep the editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'text' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'polyline' && (!isMobile || selectionSource === 'click') && (
          <PolylineEditor
            polyline={selectedItem.item}
            onUpdate={(updates) => {
              updateActiveFloor(f => ({
                ...f,
                polylines: (f.polylines || []).map(p =>
                  p.id === selectedItem.item.id ? { ...p, ...updates } : p
                )
              }));
              // Also update selectedItems to keep the editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'polyline' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            onVertexSelect={setSelectedVertexIndex}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'hatch' && (!isMobile || selectionSource === 'click') && (
          <HatchEditor
            hatch={selectedItem.item}
            onUpdate={(updates) => {
              updateActiveFloor(f => ({
                ...f,
                hatches: (f.hatches || []).map(h =>
                  h.id === selectedItem.item.id ? { ...h, ...updates } : h
                )
              }));
              // Also update selectedItems to keep the editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'hatch' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            onVertexSelect={setSelectedVertexIndex}
            isMobile={isMobile}
          />
        )}
        {selectedItem?.type === 'room' && (!isMobile || selectionSource === 'click') && (
          <RoomEditor
            room={activeFloor?.rooms?.find(r => r.id === selectedItem.item.id) || selectedItem.item}
            onUpdate={(updates) => {
              updateActiveFloor(f => ({
                ...f,
                rooms: (f.rooms || []).map(r =>
                  r.id === selectedItem.item.id ? { ...r, ...updates } : r
                )
              }));
              // Also update selectedItems to keep the editor in sync
              setSelectedItems(prev => prev.map(s => {
                if (s.type === 'room' && s.item?.id === selectedItem.item.id) {
                  return { ...s, item: { ...s.item, ...updates } };
                }
                return s;
              }));
            }}
            onDelete={() => deleteSelected(selectedItems)}
            onVertexSelect={setSelectedVertexIndex}
            isMobile={isMobile}
          />
        )}
        {/* HVAC Duct Editor */}
        {hvacMode && selectedDuctId && (() => {
          const selectedDuct = (activeFloor?.ducts || []).find(d => d.id === selectedDuctId);
          if (!selectedDuct) return null;
          return (
            <DuctEditor
              duct={selectedDuct}
              onUpdate={(updates) => handleUpdateDuct(selectedDuctId, updates)}
              onDelete={() => handleDeleteDuct(selectedDuctId)}
              onClose={() => setSelectedDuctId(null)}
              isMobile={isMobile}
            />
          );
        })()}

        {/* HVAC Vent Editor */}
        {hvacMode && selectedVentId && (() => {
          const selectedVent = (activeFloor?.vents || []).find(v => v.id === selectedVentId);
          if (!selectedVent) return null;
          return (
            <VentEditor
              vent={selectedVent}
              onUpdate={(updates) => handleUpdateVent(selectedVentId, updates)}
              onDelete={() => handleDeleteVent(selectedVentId)}
              onClose={() => setSelectedVentId(null)}
              isMobile={isMobile}
            />
          );
        })()}

        {/* HVAC Equipment Editor */}
        {hvacMode && selectedEquipmentId && (() => {
          const selectedEquip = (activeFloor?.equipment || []).find(e => e.id === selectedEquipmentId);
          if (!selectedEquip) return null;
          return (
            <EquipmentEditor
              equipment={selectedEquip}
              onUpdate={(updates) => handleUpdateEquipment(selectedEquipmentId, updates)}
              onDelete={() => handleDeleteEquipment(selectedEquipmentId)}
              onClose={() => setSelectedEquipmentId(null)}
              isMobile={isMobile}
            />
          );
        })()}

        {/* HVAC Vent Placer */}
        {hvacMode && showVentPlacer && (
          <VentPlacer
            ventType={ventType}
            setVentType={setVentType}
            ventSize={ventSize}
            setVentSize={setVentSize}
            onClose={() => setShowVentPlacer(false)}
            isMobile={isMobile}
          />
        )}

        {/* HVAC Equipment Library */}
        {hvacMode && showEquipmentLibrary && (
          <EquipmentLibrary
            equipmentType={equipmentType}
            setEquipmentType={setEquipmentType}
            equipmentSize={equipmentSize}
            setEquipmentSize={setEquipmentSize}
            onClose={() => setShowEquipmentLibrary(false)}
            isMobile={isMobile}
          />
        )}

        {/* HVAC Room Data Panel (Manual J) */}
        {hvacMode && showRoomDataPanel && selectedRoomForData && (
          <RoomDataPanel
            room={selectedRoomForData}
            roomData={(activeFloor?.roomData || []).find(rd => rd.roomId === selectedRoomForData.id)}
            onUpdateRoomData={handleUpdateRoomData}
            activeFloor={activeFloor}
            onClose={() => {
              setShowRoomDataPanel(false);
              setSelectedRoomForData(null);
            }}
            isMobile={isMobile}
          />
        )}

        {/* HVAC Layers Panel */}
        {hvacMode && showHVACLayers && (
          <HVACLayers
            layers={hvacLayerState}
            onUpdateLayer={(layerId, state) => {
              setHvacLayerState(prev => ({
                ...prev,
                [layerId]: state,
              }));
            }}
            onClose={() => setShowHVACLayers(false)}
            isMobile={isMobile}
          />
        )}

        {/* HVAC Summary Panel */}
        {hvacMode && showHVACSummary && (
          <HVACSummary
            activeFloor={activeFloor}
            designConditions={designConditions}
            onClose={() => setShowHVACSummary(false)}
            isMobile={isMobile}
          />
        )}

        {/* Load Calculation Panel */}
        {hvacMode && showLoadCalcPanel && (
          <LoadCalculationPanel
            rooms={activeFloor?.rooms || []}
            roomsData={activeFloor?.roomData || []}
            designConditions={designConditions}
            onDesignConditionsChange={setDesignConditions}
            showLoadLabels={showLoadLabels}
            onToggleLoadLabels={() => setShowLoadLabels(!showLoadLabels)}
            onClose={() => setShowLoadCalcPanel(false)}
            isMobile={isMobile}
          />
        )}

        {/* Ductulator */}
        {hvacMode && showDuctulator && (
          <Ductulator
            onClose={() => setShowDuctulator(false)}
            isMobile={isMobile}
          />
        )}

        {/* Floor Joist Calculator */}
        {structuralMode && showJoistCalc && (
          <FloorJoistCalculator
            onClose={() => setShowJoistCalc(false)}
            isMobile={isMobile}
          />
        )}

        {/* Beam & Header Calculator */}
        {structuralMode && showBeamCalc && (
          <BeamHeaderCalculator
            onClose={() => setShowBeamCalc(false)}
            isMobile={isMobile}
          />
        )}

        {/* Joist Bay Editor */}
        {structuralMode && selectedJoistId && (() => {
          try {
            const joist = activeFloor?.joists?.find(j => j.id === selectedJoistId);
            if (!joist) return null;
            return (
              <JoistEditor
                joist={joist}
                onUpdate={(updates) => handleUpdateJoist(selectedJoistId, updates)}
                onDelete={() => handleDeleteJoist(selectedJoistId)}
                onClose={() => setSelectedJoistId(null)}
                isMobile={isMobile}
              />
            );
          } catch (e) {
            console.warn('JoistEditor render error:', e);
            return null;
          }
        })()}

        {/* HVAC Export Modal */}
        {hvacMode && showHVACExport && (
          <HVACExportModal
            activeFloor={activeFloor}
            projectInfo={projectInfo}
            canvasRef={canvasRef}
            onClose={() => setShowHVACExport(false)}
            onShare={() => setShowShareModal(true)}
            isMobile={isMobile}
          />
        )}

        {/* HVAC Stamps Selector */}
        {hvacMode && showHVACStamps && (
          <HVACStamps
            selectedStamp={selectedStampType}
            onSelectStamp={(stampType) => {
              setSelectedStampType(stampType);
              setShowHVACStamps(false);
              setHvacTool('stamp');
            }}
            onClose={() => setShowHVACStamps(false)}
            isMobile={isMobile}
          />
        )}

        {/* HVAC Label Placer */}
        {hvacMode && showLabelPlacer && (
          <LabelPlacer
            labelType={labelPlacerType}
            onSelectLabel={(labelConfig) => {
              setPendingLabel(labelConfig);
              setShowLabelPlacer(false);
              setHvacTool(labelPlacerType === 'CFM' ? 'cfm-label' : 'duct-label');
            }}
            onClose={() => setShowLabelPlacer(false)}
            isMobile={isMobile}
          />
        )}

        {/* HVAC Stamp Editor */}
        {hvacMode && selectedHvacStamp && activeFloor?.hvacStamps?.find(s => s.id === selectedHvacStamp) && (
          <StampEditor
            stamp={activeFloor.hvacStamps.find(s => s.id === selectedHvacStamp)}
            onUpdate={(updatedStamp) => handleUpdateStamp(selectedHvacStamp, updatedStamp)}
            onDelete={() => handleDeleteStamp(selectedHvacStamp)}
            onClose={() => setSelectedHvacStamp(null)}
            isMobile={isMobile}
          />
        )}

        {/* HVAC Label Editor */}
        {hvacMode && selectedHvacLabel && activeFloor?.hvacLabels?.find(l => l.id === selectedHvacLabel) && (
          <LabelEditor
            label={activeFloor.hvacLabels.find(l => l.id === selectedHvacLabel)}
            onUpdate={(updatedLabel) => handleUpdateLabel(selectedHvacLabel, updatedLabel)}
            onDelete={() => handleDeleteLabel(selectedHvacLabel)}
            onClose={() => setSelectedHvacLabel(null)}
            isMobile={isMobile}
          />
        )}

        {/* 3D View */}
        {show3D && (
          <ThreeDView
            activeFloor={activeFloor}
            isMobile={isMobile}
            onClose={() => setShow3D(false)}
          />
        )}
      </div>

      {/* Status Bar - desktop only */}
      {!isMobile && (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          background: 'linear-gradient(180deg, rgba(15,20,28,0.95) 0%, rgba(8,12,16,0.98) 100%)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          fontSize: '11px',
          color: '#6080a0',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <FileMenu
            onNew={handleNewFile}
            onFromTemplate={() => setShowTemplateSelector(true)}
            onSave={handleSaveFile}
            onLoad={handleLoadFile}
            onRecent={() => setShowRecentProjects(true)}
            onExport={() => {
              if (requirePro(PRO_FEATURES.PDF_EXPORT, 'PDF Export')) {
                setActivePanel('export');
              }
            }}
            onExportAnnotatedPDF={() => {
              if (requirePro(PRO_FEATURES.PDF_EXPORT, 'PDF Export')) {
                setShowPDFExportModal(true);
              }
            }}
            onPrint={handlePrint}
            isMobile={isMobile}
            hasPdfLayer={!!pdfLayer?.dataUrl}
          />
          <EditMenu
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            onSelectAll={selectAllOnFloor}
            onDeselectAll={() => setSelectedItems([])}
            onDelete={() => deleteSelected(selectedItems)}
            onDetectRooms={detectRooms}
            onCopy={handleCopy}
            onPaste={handlePaste}
            hasClipboard={hasClipboard}
            hasSelection={selectedItems.length > 0}
          />
          {clipboardMessage && (
            <span
              style={{
                color: '#00c8ff',
                background: 'rgba(0,200,255,0.12)',
                border: '1px solid rgba(0,200,255,0.25)',
                borderRadius: '4px',
                padding: '3px 8px',
                fontSize: '11px',
                whiteSpace: 'nowrap',
              }}
            >
              {clipboardMessage}
            </span>
          )}
          <button
            onClick={() => setActivePanel(activePanel === 'settings' ? null : 'settings')}
            style={{
              background: activePanel === 'settings' ? 'rgba(0,200,255,0.2)' : 'transparent',
              border: 'none',
              color: activePanel === 'settings' ? '#00c8ff' : '#6080a0',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Settings"
          >
            <TOOLBAR_ICONS.gear color={activePanel === 'settings' ? '#00c8ff' : '#6080a0'} /> Settings
          </button>
          <button
            onClick={() => {
              if (requirePro(PRO_FEATURES.ESTIMATES, 'Estimates')) {
                setShowEstimateList(true);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#6080a0',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Project Estimates (Pro)"
          >
            <TOOLBAR_ICONS.estimate color="#6080a0" /> Estimates{estimates.length > 0 ? ` (${estimates.length})` : ''}
          </button>
          <button
            onClick={() => {
              if (requirePro(PRO_FEATURES.INVOICES, 'Invoices')) {
                setShowInvoiceList(true);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#6080a0',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Project Invoices (Pro)"
          >
            <TOOLBAR_ICONS.invoice color="#6080a0" /> Invoices{invoices.length > 0 ? ` (${invoices.length})` : ''}
          </button>
          <button
            onClick={() => setShowClientManager(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#6080a0',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Saved Clients"
          >
            <TOOLBAR_ICONS.clients color="#6080a0" /> Clients
          </button>
          <button
            onClick={() => {
              if (requirePro(PRO_FEATURES.COMPANY_PROFILE, 'Company Profile')) {
                setShowCompanyProfile(true);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#6080a0',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Company Profile (Pro)"
          >
            <TOOLBAR_ICONS.company color="#6080a0" /> Company
          </button>
          <button
            onClick={() => setShowHelpPanel(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#6080a0',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Help & Tutorial"
          >
            <TOOLBAR_ICONS.help color="#6080a0" /> Help
          </button>
          <span>
            Tool: <span style={{ color: '#00c8ff' }}>{tool}</span>
          </span>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowFloorSelector(!showFloorSelector)}
              style={{
                background: showFloorSelector ? 'rgba(0,200,255,0.2)' : 'transparent',
                border: 'none',
                color: '#8090a0',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              Floor: <span style={{ color: '#00c8ff' }}>{activeFloor?.name}</span>
              <span style={{ fontSize: '8px', marginLeft: '2px' }}>▼</span>
            </button>
            {showFloorSelector && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  marginBottom: '4px',
                  background: 'rgba(12,18,24,0.98)',
                  border: '1px solid rgba(0,200,255,0.3)',
                  borderRadius: '6px',
                  padding: '8px',
                  minWidth: '180px',
                  boxShadow: '0 -4px 16px rgba(0,0,0,0.4)',
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ marginBottom: '8px', fontSize: '10px', color: '#6080a0', textTransform: 'uppercase' }}>
                  Floors
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '8px' }}>
                  {floors.map((floor) => (
                    <div
                      key={floor.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 8px',
                        background: floor.id === activeFloorId ? 'rgba(0,200,255,0.15)' : 'transparent',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setActiveFloorId(floor.id);
                        setShowFloorSelector(false);
                      }}
                    >
                      <span style={{ flex: 1, color: floor.id === activeFloorId ? '#00c8ff' : '#d0d8e0', fontSize: '12px' }}>
                        {floor.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newName = prompt('Rename floor:', floor.name);
                          if (newName && newName.trim()) {
                            renameFloor(floor.id, newName.trim());
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#8090a0',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          fontSize: '10px',
                          opacity: 0.6,
                        }}
                        title="Rename floor"
                      >
                        ✎
                      </button>
                      {floors.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Delete "${floor.name}"?`)) {
                              removeFloor(floor.id);
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ff6666',
                            cursor: 'pointer',
                            padding: '2px 4px',
                            fontSize: '10px',
                            opacity: 0.6,
                          }}
                          title="Delete floor"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '4px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                  <button
                    onClick={() => {
                      const name = prompt('Floor name:', `Floor ${floors.length + 1}`);
                      if (name) {
                        addFloor(name);
                        setShowFloorSelector(false);
                      }
                    }}
                    style={{
                      flex: 1,
                      background: 'rgba(0,200,255,0.15)',
                      border: '1px solid rgba(0,200,255,0.3)',
                      borderRadius: '4px',
                      color: '#00c8ff',
                      cursor: 'pointer',
                      padding: '6px 8px',
                      fontSize: '11px',
                    }}
                  >
                    + Add Floor
                  </button>
                  <button
                    onClick={() => {
                      duplicateFloor(activeFloorId);
                      setShowFloorSelector(false);
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '4px',
                      color: '#8090a0',
                      cursor: 'pointer',
                      padding: '6px 8px',
                      fontSize: '11px',
                    }}
                    title="Duplicate current floor"
                  >
                    ⧉
                  </button>
                </div>
              </div>
            )}
          </div>
          {selectedItems.length > 0 && (
            <span>
              Selected: <span style={{ color: '#00ffaa' }}>{selectedItems.length}</span>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          {terrain && pdfLayer?.geo && (
            <ElevationReadout
              cursorListenerRef={cursorListenerRef}
              screenToWorld={screenToWorld}
              terrain={terrain}
              layer={pdfLayer}
              arenaPoints={arenaRoom?.points || null}
              grading={gradingResult?.result || null}
            />
          )}
          <span>
            Walls: <span style={{ color: '#00c8ff' }}>{activeFloor?.walls?.length || 0}</span>
          </span>
          <span>
            Zoom: <span style={{ color: '#00c8ff' }}>{Math.round(zoom * 100)}%</span>
          </span>
        </div>
      </div>
      )}

      {/* Elevation View Modal */}
      {viewMode === 'elevations' && (
        <ElevationView
          activeFloor={activeFloor}
          floors={floors}
          isMobile={isMobile}
          onClose={() => setViewMode('model')}
          onUpdateRoof={updateRoof}
          onRemoveRoof={removeRoof}
          onUpdatePost={updatePost}
          onRemovePost={removePost}
          onUpdateWallHeight={(newHeightInches) => {
            // Update all walls to the new height
            activeFloor?.walls?.forEach(wall => {
              updateWall(wall.id, { height: newHeightInches });
            });
          }}
          onUpdateFloorElevation={updateFloorElevation}
          onAddWindow={addWindow}
          onAddDoor={addDoor}
          onUpdateWindow={updateWindowOnFloor}
          onUpdateDoor={updateDoorOnFloor}
          onDeleteWindow={removeWindowOnFloor}
          onDeleteDoor={removeDoorOnFloor}
          activeFloorId={activeFloorId}
          onSetActiveFloorId={setActiveFloorId}
          elevationSettings={elevationSettings}
          onUpdateElevationSettings={setElevationSettings}
          formatMeasurement={formatMeasurementFn}
          units={units}
        />
      )}

      {/* Paper Space View */}
      {viewMode === 'paper' && (
        <PaperSpaceView
          activeFloor={activeFloor}
          activeSheet={activeSheet}
          isMobile={isMobile}
          wallDetailLevel={wallDetailLevel}
          onClose={() => setViewMode('model')}
          onUpdateSheet={handleUpdateSheet}
          projectInfo={projectInfo}
          onUpdateProjectInfo={setProjectInfo}
        />
      )}

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSelectAll={selectAllOnFloor}
        onDeselectAll={() => setSelectedItems([])}
        onDelete={() => deleteSelected(selectedItems)}
        onDetectRooms={detectRooms}
        onCopy={handleCopy}
        onPaste={handlePaste}
        hasClipboard={hasClipboard}
        hasSelection={selectedItems.length > 0}
        onShowSheets={() => setActivePanel('sheets')}
        onShowLayers={() => setActivePanel('layers')}
        onShow3D={() => (hasSite3D ? setShowSite3D(true) : setShow3D(!show3D))}
        onShowSettings={() => setActivePanel('settings')}
        onShowPdf={() => {
          if (pdfLayer?.dataUrl || requirePro(PRO_FEATURES.PDF_IMPORT, 'PDF Import')) {
            setActivePanel(pdfLayer?.dataUrl ? 'pdf-controls' : 'pdf-import');
          }
        }}
        onShowMaterials={() => {
          if (requirePro(PRO_FEATURES.MATERIAL_CALCULATOR, 'Material Calculator')) {
            setShowMaterialCalculator(true);
          }
        }}
        onShowEstimates={() => {
          if (requirePro(PRO_FEATURES.ESTIMATES, 'Estimates')) {
            setShowEstimateList(true);
          }
        }}
        onShowInvoices={() => {
          if (requirePro(PRO_FEATURES.INVOICES, 'Invoices')) {
            setShowInvoiceList(true);
          }
        }}
        onShowClients={() => setShowClientManager(true)}
        onShowCompanyProfile={() => {
          if (requirePro(PRO_FEATURES.COMPANY_PROFILE, 'Company Profile')) {
            setShowCompanyProfile(true);
          }
        }}
        onShowTemplates={() => setShowTemplateSelector(true)}
        onExport={() => {
          if (requirePro(PRO_FEATURES.PDF_EXPORT, 'PDF Export')) {
            setActivePanel('export');
          }
        }}
        onExportAnnotatedPDF={() => {
          if (requirePro(PRO_FEATURES.PDF_EXPORT, 'PDF Export')) {
            setShowPDFExportModal(true);
          }
        }}
        onSave={handleSaveFile}
        onLoad={handleLoadFile}
        onNew={handleNewFile}
        onRecent={() => setShowRecentProjects(true)}
        onShare={() => setShowShareModal(true)}
        show3D={show3D}
        showLayersPanel={activePanel === 'layers'}
        showSheetsPanel={activePanel === 'sheets'}
        hasPdf={!!pdfLayer?.dataUrl}
        floors={floors}
        activeFloorId={activeFloorId}
        onSelectFloor={setActiveFloorId}
        onAddFloor={addFloor}
        onRemoveFloor={removeFloor}
        onDuplicateFloor={duplicateFloor}
        onRenameFloor={renameFloor}
        currentTool={tool}
        onToolSelect={setTool}
        selectionMode={selectionMode}
        onSelectionModeChange={setSelectionMode}
        onShowHelp={() => setShowHelpPanel(true)}
      />

      {/* Onboarding Flow */}
      {showOnboarding && (
        <OnboardingFlow
          onComplete={() => setShowOnboarding(false)}
          onSkip={() => setShowOnboarding(false)}
        />
      )}

      {/* Help Panel */}
      {showHelpPanel && (
        <HelpPanel
          onClose={() => setShowHelpPanel(false)}
          onShowOnboarding={() => {
            setShowHelpPanel(false);
            setShowOnboarding(true);
          }}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

export default App;
